import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import Layout from '../components/Layout';
import { api } from '../lib/api';

// ============================================================================
// TYPES
// ============================================================================

interface SalesOrder {
  id: string;
  orderNumber: number;
  status: 'draft' | 'offered' | 'accepted' | 'delivered';
  description: string | null;
  customer: string | null;
  priceOffered: number | null;
  priceAccepted: number | null;
  buildCost: number | null;
  additionalCost: number | null;
  marginOffered: number | null;
  marginFinal: number | null;
  defaultEngineeringComplex: string | null;
  defaultRefinery: string | null;
  createdAt: string;
  updatedAt: string;
  nodes: SalesOrderNode[];
}

interface SalesOrderNode {
  id: string;
  salesOrderId: string;
  parentId: string | null;
  typeId: number;
  quantity: number;
  isBuild: boolean;
  blueprintId: number | null;
  blueprintActivity: string | null;
  runs: number;
  materialEfficiency: number;
  timeEfficiency: number;
  structureId: string | null;
  rigOverride: string | null; // 'None' | 'T1' | 'T2' - overrides structure's material/reaction rig
  timeRigOverride: string | null; // 'None' | 'T1' | 'T2' - overrides structure's time rig
  priceMode: string;
  customPrice: number | null;
  unitPrice: number | null;
  totalPrice: number | null;
  expanded: boolean;
  depth: number;
  // Shared component fields
  isGhostRef: boolean;
  definingNodeId: string | null;
  usesOwnedBlueprint: boolean;
  sharedCount?: number; // Number of ghost refs pointing to this defining node
  createdAt: string;
  updatedAt: string;
  // Enriched from backend
  typeName: string;
  priceBuy: number | null;
  priceSplit: number | null;
  priceSell: number | null;
  iconId: number | null;
  buildableInfo: { blueprintId: number; outputQuantity: number; activity: string } | null;
  children: SalesOrderNode[];
}

interface EveType {
  id: number;
  name: string;
  isSkin?: boolean;
  isBlueprint?: boolean;
}

interface ParsedItem {
  name: string;
  quantity: number;
  typeId?: number;
  typeName?: string;
  found: boolean;
}

interface Structure {
  id: string;
  name: string;
  structureTypeId: number;
  materialRig: string | null;
  reactionRig: string | null;
  timeRig: string | null;
  facilityTax: number;
  solarSystem: {
    id: number;
    nameEn: string;
    securityStatus: number | null;
    costIndices: {
      manufacturing: number;
      reaction: number;
    } | null;
  };
  structureType: {
    id: number;
    nameEn: string;
    iconId: number | null;
  };
}

interface OwnedBlueprint {
  itemId: string;
  characterId: string;
  characterName: string;
  locationId: string;
  locationName: string | null;
  solarSystemName: string | null;
  isOriginal: boolean;
  isCopy: boolean;
  runs: number;
  materialEfficiency: number;
  timeEfficiency: number;
}


// ============================================================================
// HELPERS
// ============================================================================

// Get icon URL for search results - handles SKINs (no icon) and Blueprints (/bp suffix)
const getSearchItemIconUrl = (typeId: number, typeName: string, size: number = 32): string | null => {
  if (typeName.endsWith(' SKIN')) return null;
  const suffix = typeName.endsWith(' Blueprint') ? 'bp' : 'icon';
  return `https://images.evetech.net/types/${typeId}/${suffix}?size=${size}`;
};

// ============================================================================
// CONSTANTS
// ============================================================================

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-600 text-gray-100',
  offered: 'bg-yellow-600 text-yellow-100',
  accepted: 'bg-green-600 text-green-100',
  delivered: 'bg-blue-600 text-blue-100',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  offered: 'Offered',
  accepted: 'Accepted',
  delivered: 'Delivered',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatISK(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  if (value >= 1e9) return (value / 1e9).toFixed(2) + ' B';
  if (value >= 1e6) return (value / 1e6).toFixed(2) + ' M';
  if (value >= 1e3) return (value / 1e3).toFixed(2) + ' K';
  return value.toFixed(2);
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Get effective price for a node based on price mode
// When useStoredPrice is true, prefer the stored unitPrice (for accepted/delivered orders)
function getEffectivePrice(node: SalesOrderNode, priceMode: 'buy' | 'split' | 'sell', useStoredPrice = false): number {
  if (node.customPrice !== null) return node.customPrice;

  // For accepted/delivered orders, use stored unitPrice if available
  if (useStoredPrice && node.unitPrice !== null) {
    return node.unitPrice;
  }

  switch (priceMode) {
    case 'buy': return node.priceBuy ?? 0;
    case 'split': return node.priceSplit ?? 0;
    case 'sell': return node.priceSell ?? 0;
    default: return node.priceBuy ?? 0;
  }
}

// Calculate total for a node (recursive for build nodes)
// Note: This is a simplified calculation without ME adjustments or job costs
function getNodeTotal(node: SalesOrderNode, priceMode: 'buy' | 'split' | 'sell', useStoredPrice = false): number {
  if (node.isBuild && node.children.length > 0) {
    return node.children.reduce((sum, child) => {
      const childTotal = getNodeTotal(child, priceMode, useStoredPrice);
      if (child.isBuild) {
        // Child produces in batches - calculate proportional cost
        const childOutputPerRun = child.buildableInfo?.outputQuantity ?? 1;
        const childRunsNeeded = Math.ceil(child.quantity / childOutputPerRun);
        const childActualProduced = childRunsNeeded * childOutputPerRun;
        const childUnitCost = childTotal / childActualProduced;
        return sum + (childUnitCost * child.quantity);
      }
      return sum + childTotal;
    }, 0);
  }
  return getEffectivePrice(node, priceMode, useStoredPrice) * node.quantity;
}

// Calculate complete build cost for a node including job costs (recursive)
function getNodeCompleteCost(
  node: SalesOrderNode,
  priceMode: 'buy' | 'split' | 'sell',
  structures: Structure[],
  adjustedPrices: Record<number, number>,
  useStoredPrice = false
): number {
  // If not building, just return buy price × quantity
  if (!node.isBuild || node.children.length === 0) {
    return getEffectivePrice(node, priceMode, useStoredPrice) * node.quantity;
  }

  // Get structure info
  const structure = structures.find(s => s.id === node.structureId) ?? null;
  const isReaction = node.blueprintActivity === 'reaction';

  // Child quantities are already ME/structure/rig adjusted by the backend
  // Calculate EIV using stored quantities
  const eiv = node.children.reduce((sum, child) => {
    const adjPrice = adjustedPrices[child.typeId] ?? 0;
    return sum + (adjPrice * child.quantity);
  }, 0);

  // Calculate job costs
  const systemCostIndex = isReaction
    ? (structure?.solarSystem?.costIndices?.reaction ?? 0)
    : (structure?.solarSystem?.costIndices?.manufacturing ?? 0);

  const systemCost = eiv * systemCostIndex;
  const structureRoleBonus = systemCost * getStructureRoleBonus(structure?.structureType?.id ?? null);
  const jobGrossCost = systemCost - structureRoleBonus;
  const sccSurcharge = eiv * 0.04;
  const facilityTax = eiv * (structure?.facilityTax ?? 0);
  const totalJobCost = jobGrossCost + sccSurcharge + facilityTax;

  // Calculate material cost (recursive for build children)
  const totalMaterialCost = node.children.reduce((sum, child) => {
    if (child.isBuild) {
      // Get the complete cost for the child's production
      const childCompleteCost = getNodeCompleteCost(child, priceMode, structures, adjustedPrices, useStoredPrice);
      // Calculate the actual quantity produced by the child
      // (runs needed × output per run, which may be MORE than child.quantity due to batch sizes)
      const childOutputPerRun = child.buildableInfo?.outputQuantity ?? 1;
      const childRunsNeeded = Math.ceil(child.quantity / childOutputPerRun);
      const childActualProduced = childRunsNeeded * childOutputPerRun;
      // Calculate unit cost (cost per item actually produced)
      const childUnitCost = childCompleteCost / childActualProduced;
      // Parent only pays for the quantity it actually needs
      return sum + (childUnitCost * child.quantity);
    }
    // For buy children, use stored quantity × price
    const price = getEffectivePrice(child, priceMode, useStoredPrice);
    return sum + (price * child.quantity);
  }, 0);

  return totalMaterialCost + totalJobCost;
}

// Calculate total complete build cost from all root nodes (including job costs)
function calculateCompleteBuildCost(
  nodes: SalesOrderNode[],
  priceMode: 'buy' | 'split' | 'sell',
  structures: Structure[],
  adjustedPrices: Record<number, number>,
  useStoredPrice = false
): number {
  return nodes.reduce((sum, node) => sum + getNodeProportionalCost(node, priceMode, structures, adjustedPrices, useStoredPrice), 0);
}

// Get the proportional cost for a node (accounts for batch production)
// If a node produces more than needed, returns cost for only what's needed
function getNodeProportionalCost(
  node: SalesOrderNode,
  priceMode: 'buy' | 'split' | 'sell',
  structures: Structure[],
  adjustedPrices: Record<number, number>,
  useStoredPrice = false
): number {
  const completeCost = getNodeCompleteCost(node, priceMode, structures, adjustedPrices, useStoredPrice);

  // If not building, the complete cost is already correct (price × quantity)
  if (!node.isBuild) {
    return completeCost;
  }

  // For build nodes, calculate proportional cost based on actual production
  const outputPerRun = node.buildableInfo?.outputQuantity ?? 1;
  const runsNeeded = Math.ceil(node.quantity / outputPerRun);
  const actualProduced = runsNeeded * outputPerRun;

  // If we produce exactly what's needed, return full cost
  if (actualProduced === node.quantity) {
    return completeCost;
  }

  // Otherwise, return proportional cost
  const unitCost = completeCost / actualProduced;
  return unitCost * node.quantity;
}

// Get structure role bonus for job costs
function getStructureRoleBonus(structureTypeId: number | null): number {
  if (!structureTypeId) return 0;
  if (structureTypeId === 35825) return 0.03; // Raitaru
  if (structureTypeId === 35826) return 0.04; // Azbel
  if (structureTypeId === 35827) return 0.05; // Sotiyo
  // Refineries for reactions
  if (structureTypeId === 35835) return 0.02; // Athanor
  if (structureTypeId === 35836) return 0.025; // Tatara
  return 0;
}

// Get rig material modifier based on rig type and security status (used for display)
function getRigsMaterialModifier(rigType: 'None' | 'T1' | 'T2', systemType: 'High' | 'Low' | 'Null'): number {
  const baseModifiers = { None: 0, T1: 0.02, T2: 0.024 };
  const systemMultipliers = { High: 1, Low: 1.9, Null: 2.1 };
  return baseModifiers[rigType] * systemMultipliers[systemType];
}

// Get system security type from security status (used for display)
function getSystemType(securityStatus: number | null): 'High' | 'Low' | 'Null' {
  const sec = securityStatus ?? 1;
  if (sec >= 0.5) return 'High';
  if (sec > 0) return 'Low';
  return 'Null';
}

// Mini pie chart component for percentage visualization
function PieChart({ percentage, isPositive, size = 20 }: { percentage: number; isPositive: boolean; size?: number }) {
  // Clamp percentage to 0-100 for the visual (full circle if >= 100)
  const displayPercent = Math.min(Math.abs(percentage), 100);
  const radius = size / 2 - 1;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = `${(displayPercent / 100) * circumference} ${circumference}`;
  const color = isPositive ? '#4ade80' : '#f87171'; // green-400 / red-400
  const bgColor = '#374151'; // gray-700

  return (
    <svg width={size} height={size} className="flex-shrink-0">
      {/* Background circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={bgColor}
        strokeWidth="2"
      />
      {/* Percentage arc */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeDasharray={strokeDasharray}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

// Parse clipboard format
function parseClipboardItems(text: string): { name: string; quantity: number }[] {
  const lines = text.trim().split('\n');
  const items: { name: string; quantity: number }[] = [];

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (!trimmedLine) continue;

    let parts = trimmedLine.split('\t');
    if (parts.length < 2) parts = trimmedLine.split(/\s{2,}/);
    if (parts.length < 2) {
      const match = trimmedLine.match(/^(.+?)\s+(\d+)$/);
      if (match) parts = [match[1], match[2]];
    }

    if (parts.length >= 2) {
      const name = parts[0].trim();
      const quantity = parseInt(parts[parts.length - 1].trim(), 10);
      if (name && !isNaN(quantity) && quantity > 0) {
        items.push({ name, quantity });
      }
    }
  }
  return items;
}

// Parse EVE fitting format
function parseFittingItems(text: string): { name: string; quantity: number }[] {
  const lines = text.trim().split('\n');
  const itemCounts = new Map<string, number>();
  let inHeader = true;

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (!trimmedLine) { inHeader = false; continue; }
    if (inHeader && trimmedLine.startsWith('[')) { inHeader = false; continue; }
    inHeader = false;

    const quantityMatch = trimmedLine.match(/^(.+?)\s+x(\d+)$/);
    if (quantityMatch) {
      const name = quantityMatch[1].trim();
      const quantity = parseInt(quantityMatch[2], 10);
      if (name && !isNaN(quantity) && quantity > 0) {
        itemCounts.set(name, (itemCounts.get(name) || 0) + quantity);
      }
    } else {
      const name = trimmedLine;
      if (name) itemCounts.set(name, (itemCounts.get(name) || 0) + 1);
    }
  }
  return Array.from(itemCounts.entries()).map(([name, quantity]) => ({ name, quantity }));
}

// ============================================================================
// TREE CONNECTOR COMPONENT (from BuildTree)
// ============================================================================

const TreeConnector = ({ isLast, hasChildren, isExpanded, onToggle }: {
  isLast: boolean;
  hasChildren: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}) => (
  <div className="relative flex-shrink-0" style={{ width: '20px', height: '40px' }}>
    <svg className="absolute inset-0" width="20" height="40">
      <line x1="8" y1="0" x2="8" y2="20" stroke="#4B5563" strokeWidth="1" />
      {!isLast && <line x1="8" y1="20" x2="8" y2="40" stroke="#4B5563" strokeWidth="1" />}
      <line x1="8" y1="20" x2="20" y2="20" stroke="#4B5563" strokeWidth="1" />
    </svg>
    {hasChildren && (
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        className="absolute z-10 w-3 h-3 flex items-center justify-center text-[10px] leading-none text-gray-400 hover:text-white bg-gray-800 border border-gray-600 rounded-sm"
        style={{ left: '2px', top: '50%', transform: 'translateY(-50%)' }}
      >
        {isExpanded ? '−' : '+'}
      </button>
    )}
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function SalesOrders() {
  const { orderNumber } = useParams<{ orderNumber?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Sales orders list state
  const [salesOrders, setSalesOrders] = useState<SalesOrder[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<SalesOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create/delete dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<SalesOrder | null>(null);

  // Tree view state - 'summary' is the special root node ID
  // Initialize from URL query param, defaulting to 'summary'
  const [selectedNodeId, setSelectedNodeIdState] = useState<string>(() => {
    return searchParams.get('node') || 'summary';
  });
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Wrapper to update both state and URL when selecting a node
  const setSelectedNodeId = useCallback((nodeId: string) => {
    setSelectedNodeIdState(nodeId);
    // Update URL query param
    if (nodeId === 'summary') {
      searchParams.delete('node');
    } else {
      searchParams.set('node', nodeId);
    }
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Add Item Dialog
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [itemSearchResults, setItemSearchResults] = useState<EveType[]>([]);
  const [itemSearching, setItemSearching] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Map<number, { type: EveType; quantity: number }>>(new Map());
  const [addingItem, setAddingItem] = useState(false);

  // Add from Clipboard Dialog
  const [showClipboardDialog, setShowClipboardDialog] = useState(false);
  const [clipboardText, setClipboardText] = useState('');
  const [clipboardItems, setClipboardItems] = useState<ParsedItem[]>([]);
  const [clipboardLookingUp, setClipboardLookingUp] = useState(false);
  const [clipboardAdding, setClipboardAdding] = useState(false);

  // Add from Fitting Dialog
  const [showFittingDialog, setShowFittingDialog] = useState(false);
  const [fittingText, setFittingText] = useState('');
  const [fittingItems, setFittingItems] = useState<ParsedItem[]>([]);
  const [fittingLookingUp, setFittingLookingUp] = useState(false);
  const [fittingAdding, setFittingAdding] = useState(false);

  // Price mode
  const [buildCostPriceMode, setBuildCostPriceMode] = useState<'buy' | 'split' | 'sell'>('buy');
  const [refreshingPrices, setRefreshingPrices] = useState(false);
  const [refreshingEIV, setRefreshingEIV] = useState(false);

  // Edit Quantities Modal
  const [showEditQuantitiesModal, setShowEditQuantitiesModal] = useState(false);
  const [editQuantities, setEditQuantities] = useState<Record<string, number>>({});
  const [multiplyValue, setMultiplyValue] = useState(1);
  const [divideValue, setDivideValue] = useState(1);
  const [savingQuantities, setSavingQuantities] = useState(false);

  // Fulfillment Settings Modal
  const [showFulfillmentSettings, setShowFulfillmentSettings] = useState(false);
  const [fulfillmentSettingsTab, setFulfillmentSettingsTab] = useState<'systems' | 'hangars'>('systems');
  const [fulfillmentSystems, setFulfillmentSystems] = useState<{
    solarSystemId: number;
    solarSystemName: string;
    salesOrderRelevant: boolean;
  }[]>([]);
  const [loadingFulfillmentSystems, setLoadingFulfillmentSystems] = useState(false);
  const [savingFulfillmentSettings, setSavingFulfillmentSettings] = useState(false);
  const [fulfillmentSearchQuery, setFulfillmentSearchQuery] = useState('');
  const [showOnlyActiveSettings, setShowOnlyActiveSettings] = useState(false);
  // Hangar settings
  const [fulfillmentHangars, setFulfillmentHangars] = useState<{
    corpHangar1: boolean;
    corpHangar2: boolean;
    corpHangar3: boolean;
    corpHangar4: boolean;
    corpHangar5: boolean;
    corpHangar6: boolean;
    corpHangar7: boolean;
  }>({
    corpHangar1: true,
    corpHangar2: true,
    corpHangar3: true,
    corpHangar4: true,
    corpHangar5: true,
    corpHangar6: true,
    corpHangar7: true,
  });
  const [loadingFulfillmentHangars, setLoadingFulfillmentHangars] = useState(false);

  // Tree filter - hide non-buildable items at level 2+
  const [filterBuildableOnly, setFilterBuildableOnly] = useState(false);

  // Loading state for node operations
  const [nodeLoading, setNodeLoading] = useState<string | null>(null);

  // Local slider values (for smooth dragging without API calls on every change)
  const [localME, setLocalME] = useState<number | null>(null);
  const [localTE, setLocalTE] = useState<number | null>(null);

  // Structures for build settings
  const [structures, setStructures] = useState<Structure[]>([]);

  // Owned blueprints for selected node (when usesOwnedBlueprint is true)
  const [ownedBlueprints, setOwnedBlueprints] = useState<OwnedBlueprint[]>([]);
  const [ownedBlueprintsExpanded, setOwnedBlueprintsExpanded] = useState(false);
  const [loadingOwnedBlueprints, setLoadingOwnedBlueprints] = useState(false);

  // Production defaults (synced from selectedOrder, but using local state for immediate UI updates)
  const [applyingDefaults, setApplyingDefaults] = useState(false);

  // Derive defaults from selectedOrder
  const defaultEngineeringComplex = selectedOrder?.defaultEngineeringComplex ?? null;
  const defaultRefinery = selectedOrder?.defaultRefinery ?? null;

  // Local inputs (for typing without API calls on every keystroke)
  const [localDescription, setLocalDescription] = useState<string>('');
  const [localPriceOffered, setLocalPriceOffered] = useState<string>('');
  const [localPriceAccepted, setLocalPriceAccepted] = useState<string>('');
  const [localAdditionalCost, setLocalAdditionalCost] = useState<string>('');
  const [localCustomPrice, setLocalCustomPrice] = useState<string>('');

  // Adjusted prices for job cost calculations
  const [adjustedPrices, setAdjustedPrices] = useState<Record<number, number>>({});

  // Fulfillment data for Level 1 items (In Stock, In Production, In Procurement)
  const [fulfillmentData, setFulfillmentData] = useState<Record<string, { inStock: number; inProduction: number; inProcurement: number }>>({});

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  useEffect(() => {
    const fetchSalesOrders = async () => {
      try {
        const response = await api.get('/sales-orders');
        setSalesOrders(response.data.salesOrders);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to fetch sales orders');
      } finally {
        setLoading(false);
      }
    };
    fetchSalesOrders();
  }, []);

  // Fetch structures for build settings
  useEffect(() => {
    const fetchStructures = async () => {
      try {
        const response = await api.get('/structures');
        // API returns array directly, not wrapped in { structures: ... }
        setStructures(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Failed to fetch structures:', err);
      }
    };
    fetchStructures();
  }, []);

  // Fetch adjusted prices when order changes
  // Create a stable key for nodes that only changes when the tree structure changes
  const nodesStructureKey = useMemo(() => {
    if (!selectedOrder?.nodes) return '';
    const collectKeys = (nodes: SalesOrderNode[]): string => {
      return nodes.map(n => `${n.id}:${n.isBuild}:${collectKeys(n.children)}`).join(',');
    };
    return collectKeys(selectedOrder.nodes);
  }, [selectedOrder?.nodes]);

  useEffect(() => {
    if (!selectedOrder || selectedOrder.nodes.length === 0) return;

    const fetchAdjustedPrices = async () => {
      // Collect all typeIds recursively
      const collectTypeIds = (nodes: SalesOrderNode[]): number[] => {
        const ids: number[] = [];
        for (const node of nodes) {
          ids.push(node.typeId);
          if (node.children?.length > 0) {
            ids.push(...collectTypeIds(node.children));
          }
        }
        return ids;
      };

      const typeIds = [...new Set(collectTypeIds(selectedOrder.nodes))];
      if (typeIds.length === 0) return;

      try {
        const response = await api.post('/market-prices/adjusted', { typeIds });
        const priceMap: Record<number, number> = {};
        for (const price of response.data.adjustedPrices) {
          priceMap[price.typeId] = price.adjustedPrice;
        }
        setAdjustedPrices(priceMap);
      } catch (err) {
        console.error('Failed to fetch adjusted prices:', err);
      }
    };
    fetchAdjustedPrices();
  }, [selectedOrder?.id, nodesStructureKey]);

  // Fetch fulfillment data (In Stock, In Production) when order changes
  useEffect(() => {
    if (!selectedOrder) {
      setFulfillmentData({});
      return;
    }

    const fetchFulfillmentData = async () => {
      try {
        const response = await api.get(`/sales-orders/${selectedOrder.id}/fulfillment`);
        setFulfillmentData(response.data.fulfillment);
      } catch (err) {
        console.error('Failed to fetch fulfillment data:', err);
        setFulfillmentData({});
      }
    };
    fetchFulfillmentData();
  }, [selectedOrder?.id]);

  useEffect(() => {
    if (orderNumber && salesOrders.length > 0) {
      const targetOrderNumber = parseInt(orderNumber, 10);
      // Only update selectedOrder if we're switching to a different order
      // or if selectedOrder is not yet set
      if (!selectedOrder || selectedOrder.orderNumber !== targetOrderNumber) {
        const order = salesOrders.find(o => o.orderNumber === targetOrderNumber);
        setSelectedOrder(order || null);
        // Reset to summary when switching to a different order
        if (selectedOrder && selectedOrder.orderNumber !== targetOrderNumber) {
          setSelectedNodeId('summary');
        }
      }
    } else if (!orderNumber) {
      setSelectedOrder(null);
    }
  }, [orderNumber, salesOrders]);

  // Sync local inputs when selected order changes
  useEffect(() => {
    if (selectedOrder) {
      setLocalDescription(selectedOrder.description ?? '');
      setLocalPriceOffered(selectedOrder.priceOffered?.toString() ?? '');
      setLocalPriceAccepted(selectedOrder.priceAccepted?.toString() ?? '');
      setLocalAdditionalCost(selectedOrder.additionalCost?.toString() ?? '');
    }
  }, [selectedOrder?.id]);

  // Sync local custom price when selected node changes
  useEffect(() => {
    if (selectedOrder && selectedNodeId && selectedNodeId !== 'summary') {
      const node = findNodeById(selectedOrder.nodes, selectedNodeId);
      setLocalCustomPrice(node?.customPrice?.toString() ?? '');
    } else {
      setLocalCustomPrice('');
    }
  }, [selectedNodeId, selectedOrder]);

  // ============================================================================
  // ORDER CRUD
  // ============================================================================

  const handleCreateOrder = async () => {
    setCreating(true);
    try {
      const response = await api.post('/sales-orders', { customer: customerName || null });
      setShowCreateDialog(false);
      setCustomerName('');
      const listResponse = await api.get('/sales-orders');
      setSalesOrders(listResponse.data.salesOrders);
      navigate(`/sales-orders/${response.data.orderNumber}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create sales order');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteOrder = async (order: SalesOrder) => {
    setDeleting(order.id);
    try {
      await api.delete(`/sales-orders/${order.id}`);
      setSalesOrders(prev => prev.filter(o => o.id !== order.id));
      setShowDeleteConfirm(null);
      if (selectedOrder?.id === order.id) navigate('/sales-orders');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete sales order');
    } finally {
      setDeleting(null);
    }
  };

  const handleOrderUpdate = useCallback((updatedOrder: SalesOrder) => {
    setSalesOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
    setSelectedOrder(updatedOrder);
  }, []);

  const handleUpdateSalesOrder = async (updates: {
    description?: string | null;
    customer?: string;
    priceOffered?: number | null;
    priceAccepted?: number | null;
    additionalCost?: number | null;
    status?: string;
    defaultEngineeringComplex?: string | null;
    defaultRefinery?: string | null;
  }) => {
    if (!selectedOrder) return;
    try {
      const response = await api.put(`/sales-orders/${selectedOrder.id}`, updates);
      const updatedOrder = response.data as SalesOrder;

      // Update selectedOrder with the full response (includes nodes)
      setSelectedOrder(updatedOrder);

      // Only update salesOrders if status changed (shown in left panel)
      // Other fields don't need to update the list
      if (updates.status !== undefined) {
        setSalesOrders(prev => prev.map(o => o.id === updatedOrder.id ? {
          ...o,
          status: updatedOrder.status,
        } : o));
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update sales order');
    }
  };

  // ============================================================================
  // NODE OPERATIONS
  // ============================================================================

  const handleBuild = async (nodeId: string, structureIdOverride?: string | null) => {
    if (!selectedOrder || nodeLoading) return;
    setNodeLoading(nodeId);
    try {
      // Use provided structureId, or determine default based on node type
      let structureId = structureIdOverride;
      if (structureId === undefined) {
        // Find the node to determine if it's a reaction
        const findNode = (nodes: SalesOrderNode[]): SalesOrderNode | undefined => {
          for (const n of nodes) {
            if (n.id === nodeId) return n;
            const found = findNode(n.children);
            if (found) return found;
          }
          return undefined;
        };
        const node = findNode(selectedOrder.nodes);
        const isReaction = node?.buildableInfo?.activity === 'reaction';
        structureId = isReaction ? defaultRefinery : defaultEngineeringComplex;
      }

      const response = await api.post(`/sales-orders/${selectedOrder.id}/nodes/${nodeId}/build`, { structureId });
      const updatedOrder = response.data as SalesOrder;

      // Find the built node and collect typeIds from its new children
      const findNodeInOrder = (nodes: SalesOrderNode[]): SalesOrderNode | undefined => {
        for (const n of nodes) {
          if (n.id === nodeId) return n;
          const found = findNodeInOrder(n.children);
          if (found) return found;
        }
        return undefined;
      };
      const builtNode = findNodeInOrder(updatedOrder.nodes);
      if (builtNode && builtNode.children.length > 0) {
        const childTypeIds = builtNode.children.map(child => child.typeId);
        // Refresh Janice prices for new materials (API handles 6-hour cache)
        await api.post('/janice-prices/fetch', { typeIds: childTypeIds });
      }

      handleOrderUpdate(updatedOrder);
      setExpandedNodes(prev => new Set(prev).add(nodeId));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to set build mode');
    } finally {
      setNodeLoading(null);
    }
  };

  const handleBuy = async (nodeId: string) => {
    if (!selectedOrder || nodeLoading) return;
    setNodeLoading(nodeId);
    try {
      const response = await api.post(`/sales-orders/${selectedOrder.id}/nodes/${nodeId}/buy`);
      handleOrderUpdate(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to set buy mode');
    } finally {
      setNodeLoading(null);
    }
  };

  const [buildingAll, setBuildingAll] = useState(false);

  const handleBuildAll = async (children: SalesOrderNode[]) => {
    if (!selectedOrder || buildingAll) return;

    // Filter to only buildable children that aren't already building
    const buildableChildren = children.filter(child => child.buildableInfo && !child.isBuild);
    if (buildableChildren.length === 0) return;

    setBuildingAll(true);
    try {
      // Build each child sequentially to avoid race conditions
      let latestOrder = selectedOrder;
      const builtNodeIds: string[] = [];
      for (const child of buildableChildren) {
        // Determine structure based on activity type
        const isReaction = child.buildableInfo?.activity === 'reaction';
        const structureId = isReaction ? defaultRefinery : defaultEngineeringComplex;
        const response = await api.post(`/sales-orders/${latestOrder.id}/nodes/${child.id}/build`, { structureId });
        latestOrder = response.data;
        builtNodeIds.push(child.id);
      }

      // Collect all typeIds from newly created children
      const collectChildTypeIds = (nodes: SalesOrderNode[], parentIds: string[]): number[] => {
        const typeIds: number[] = [];
        const parentIdSet = new Set(parentIds);
        const traverse = (nodeList: SalesOrderNode[]) => {
          for (const node of nodeList) {
            if (parentIdSet.has(node.id)) {
              // This is a built node - collect its children's typeIds
              node.children.forEach(child => typeIds.push(child.typeId));
            }
            traverse(node.children);
          }
        };
        traverse(nodes);
        return typeIds;
      };

      const newTypeIds = collectChildTypeIds(latestOrder.nodes, builtNodeIds);
      if (newTypeIds.length > 0) {
        // Refresh Janice prices for new materials (API handles 6-hour cache)
        await api.post('/janice-prices/fetch', { typeIds: newTypeIds });
      }

      handleOrderUpdate(latestOrder);
      // Expand all newly built nodes
      setExpandedNodes(prev => {
        const newSet = new Set(prev);
        buildableChildren.forEach(child => newSet.add(child.id));
        return newSet;
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to build all');
    } finally {
      setBuildingAll(false);
    }
  };

  const [buildingTree, setBuildingTree] = useState<{
    active: boolean;
    phase: 'building' | 'fetching-prices' | 'done';
    nodeName?: string;
  }>({ active: false, phase: 'building' });

  const handleBuildCompleteTree = async (nodeId: string) => {
    if (!selectedOrder || buildingTree.active) return;

    // Find the node name for display
    const findNodeName = (nodes: SalesOrderNode[]): string | undefined => {
      for (const n of nodes) {
        if (n.id === nodeId) return n.typeName;
        const found = findNodeName(n.children);
        if (found) return found;
      }
      return undefined;
    };
    const nodeName = findNodeName(selectedOrder.nodes);

    setBuildingTree({ active: true, phase: 'building', nodeName });
    try {
      const response = await api.post(`/sales-orders/${selectedOrder.id}/nodes/${nodeId}/build-tree`, {
        structureId: defaultEngineeringComplex,
        refineryId: defaultRefinery,
      });
      const updatedOrder = response.data as SalesOrder;

      // Update phase to fetching prices
      setBuildingTree(prev => ({ ...prev, phase: 'fetching-prices' }));

      // Collect all typeIds from the updated tree to refresh prices
      const collectAllTypeIds = (nodes: SalesOrderNode[]): number[] => {
        const typeIds: number[] = [];
        const traverse = (nodeList: SalesOrderNode[]) => {
          for (const node of nodeList) {
            typeIds.push(node.typeId);
            traverse(node.children);
          }
        };
        traverse(nodes);
        return typeIds;
      };

      const allTypeIds = collectAllTypeIds(updatedOrder.nodes);
      if (allTypeIds.length > 0) {
        // Refresh Janice prices for all materials (API handles 6-hour cache)
        await api.post('/janice-prices/fetch', { typeIds: allTypeIds });
      }

      handleOrderUpdate(updatedOrder);

      // Expand all nodes in the tree recursively
      const collectAllNodeIds = (nodes: SalesOrderNode[]): string[] => {
        const ids: string[] = [];
        const traverse = (nodeList: SalesOrderNode[]) => {
          for (const node of nodeList) {
            if (node.isBuild && !node.isGhostRef) {
              ids.push(node.id);
            }
            traverse(node.children);
          }
        };
        traverse(nodes);
        return ids;
      };

      const allBuildNodeIds = collectAllNodeIds(updatedOrder.nodes);
      setExpandedNodes(prev => {
        const newSet = new Set(prev);
        allBuildNodeIds.forEach(id => newSet.add(id));
        return newSet;
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to build complete tree');
    } finally {
      setBuildingTree({ active: false, phase: 'done' });
    }
  };

  const [buildingAllTrees, setBuildingAllTrees] = useState<{
    active: boolean;
    phase: 'building' | 'fetching-prices' | 'done';
  }>({ active: false, phase: 'building' });

  const handleBuildAllTrees = async () => {
    if (!selectedOrder || buildingAllTrees.active) return;

    setBuildingAllTrees({ active: true, phase: 'building' });
    try {
      const response = await api.post(`/sales-orders/${selectedOrder.id}/build-all-trees`, {
        structureId: defaultEngineeringComplex,
        refineryId: defaultRefinery,
      });
      const updatedOrder = response.data as SalesOrder;

      // Update phase to fetching prices
      setBuildingAllTrees(prev => ({ ...prev, phase: 'fetching-prices' }));

      // Collect all typeIds from the updated tree to refresh prices
      const collectAllTypeIds = (nodes: SalesOrderNode[]): number[] => {
        const typeIds: number[] = [];
        const traverse = (nodeList: SalesOrderNode[]) => {
          for (const node of nodeList) {
            typeIds.push(node.typeId);
            traverse(node.children);
          }
        };
        traverse(nodes);
        return typeIds;
      };

      const allTypeIds = collectAllTypeIds(updatedOrder.nodes);
      if (allTypeIds.length > 0) {
        // Refresh Janice prices for all materials (API handles 6-hour cache)
        await api.post('/janice-prices/fetch', { typeIds: allTypeIds });
      }

      handleOrderUpdate(updatedOrder);

      // Expand all build nodes in the tree recursively
      const collectAllNodeIds = (nodes: SalesOrderNode[]): string[] => {
        const ids: string[] = [];
        const traverse = (nodeList: SalesOrderNode[]) => {
          for (const node of nodeList) {
            if (node.isBuild && !node.isGhostRef) {
              ids.push(node.id);
            }
            traverse(node.children);
          }
        };
        traverse(nodes);
        return ids;
      };

      const allBuildNodeIds = collectAllNodeIds(updatedOrder.nodes);
      setExpandedNodes(prev => {
        const newSet = new Set(prev);
        allBuildNodeIds.forEach(id => newSet.add(id));
        return newSet;
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to build all trees');
    } finally {
      setBuildingAllTrees({ active: false, phase: 'done' });
    }
  };

  const handleDeleteNode = async (nodeId: string) => {
    if (!selectedOrder || nodeLoading) return;
    setNodeLoading(nodeId);
    try {
      const response = await api.delete(`/sales-orders/${selectedOrder.id}/nodes/${nodeId}`);
      handleOrderUpdate(response.data);
      if (selectedNodeId === nodeId) setSelectedNodeId('summary');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete node');
    } finally {
      setNodeLoading(null);
    }
  };

  const handleApplyProductionDefaults = async () => {
    if (!selectedOrder || applyingDefaults) return;

    setApplyingDefaults(true);
    try {
      // Use the dedicated endpoint to apply structures to all build nodes
      const response = await api.post(`/sales-orders/${selectedOrder.id}/apply-structures`, {
        engineeringComplexId: defaultEngineeringComplex || null,
        refineryId: defaultRefinery || null,
      });
      handleOrderUpdate(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to apply production defaults');
    } finally {
      setApplyingDefaults(false);
    }
  };

  const handleUpdateNodeCustomPrice = async (nodeId: string, customPrice: number | null) => {
    if (!selectedOrder) return;
    try {
      const response = await api.put(`/sales-orders/${selectedOrder.id}/nodes/${nodeId}`, { customPrice });
      handleOrderUpdate(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update custom price');
    }
  };

  const handleUpdateNodeSettings = async (nodeId: string, settings: {
    materialEfficiency?: number;
    timeEfficiency?: number;
    structureId?: string | null;
    rigOverride?: string | null;
    timeRigOverride?: string | null;
  }) => {
    if (!selectedOrder) return;
    try {
      const response = await api.put(`/sales-orders/${selectedOrder.id}/nodes/${nodeId}`, settings);
      handleOrderUpdate(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update node settings');
    }
  };

  // ============================================================================
  // PRICE REFRESH
  // ============================================================================

  const collectTypeIds = (nodes: SalesOrderNode[]): number[] => {
    const typeIds: number[] = [];
    const collect = (nodeList: SalesOrderNode[]) => {
      for (const node of nodeList) {
        typeIds.push(node.typeId);
        if (node.children.length > 0) collect(node.children);
      }
    };
    collect(nodes);
    return [...new Set(typeIds)];
  };

  const handleRefreshPrices = async () => {
    if (!selectedOrder || selectedOrder.nodes.length === 0) return;
    setRefreshingPrices(true);
    try {
      const typeIds = collectTypeIds(selectedOrder.nodes);
      await api.post('/janice-prices/fetch', { typeIds });
      const response = await api.get(`/sales-orders/${selectedOrder.id}`);
      handleOrderUpdate(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to refresh prices');
    } finally {
      setRefreshingPrices(false);
    }
  };

  const handleRefreshEIV = async () => {
    if (!selectedOrder || selectedOrder.nodes.length === 0) return;
    setRefreshingEIV(true);
    try {
      const typeIds = collectTypeIds(selectedOrder.nodes);
      const response = await api.post('/market-prices/adjusted', { typeIds });
      const priceMap: Record<number, number> = {};
      for (const price of response.data.adjustedPrices) {
        priceMap[price.typeId] = price.adjustedPrice;
      }
      setAdjustedPrices(priceMap);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to refresh EIV');
    } finally {
      setRefreshingEIV(false);
    }
  };

  // ============================================================================
  // TYPE SEARCH & ADD ITEMS
  // ============================================================================

  const searchTypes = useCallback(async (query: string) => {
    if (query.length < 2) { setItemSearchResults([]); return; }
    setItemSearching(true);
    try {
      const response = await api.get(`/sales-orders/types/search?q=${encodeURIComponent(query)}`);
      setItemSearchResults(response.data.types);
    } catch (err) {
      setItemSearchResults([]);
    } finally {
      setItemSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (showAddItemDialog) searchTypes(itemSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [itemSearchQuery, showAddItemDialog, searchTypes]);

  const handleAddItems = async () => {
    if (!selectedOrder || selectedItems.size === 0) return;

    const nodes = Array.from(selectedItems.values())
      .filter(item => item.quantity > 0)
      .map(item => ({ typeId: item.type.id, quantity: item.quantity }));

    if (nodes.length === 0) return;

    setAddingItem(true);
    try {
      const response = await api.post(`/sales-orders/${selectedOrder.id}/nodes`, { nodes });
      handleOrderUpdate(response.data);
      setShowAddItemDialog(false);
      setItemSearchQuery('');
      setItemSearchResults([]);
      setSelectedItems(new Map());
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add items');
    } finally {
      setAddingItem(false);
    }
  };

  const toggleItemSelection = (type: EveType) => {
    setSelectedItems(prev => {
      const next = new Map(prev);
      if (next.has(type.id)) {
        next.delete(type.id);
      } else {
        next.set(type.id, { type, quantity: 1 });
      }
      return next;
    });
  };

  const updateItemQuantity = (typeId: number, quantity: number) => {
    setSelectedItems(prev => {
      const next = new Map(prev);
      const item = next.get(typeId);
      if (item) {
        next.set(typeId, { ...item, quantity: Math.max(1, quantity) });
      }
      return next;
    });
  };

  const handleParseClipboard = async () => {
    const parsed = parseClipboardItems(clipboardText);
    if (parsed.length === 0) { setClipboardItems([]); return; }

    setClipboardLookingUp(true);
    try {
      const response = await api.post('/sales-orders/types/lookup', { names: parsed.map(p => p.name) });
      const lookupMap = new Map<string, { typeId: number; typeName: string }>();
      for (const item of response.data.types) {
        if (item.typeId) lookupMap.set(item.name.toLowerCase(), { typeId: item.typeId, typeName: item.typeName });
      }
      setClipboardItems(parsed.map(p => {
        const found = lookupMap.get(p.name.toLowerCase());
        return { ...p, typeId: found?.typeId, typeName: found?.typeName, found: !!found };
      }));
    } catch (err) {
      console.error('Failed to lookup types:', err);
    } finally {
      setClipboardLookingUp(false);
    }
  };

  const handleAddClipboardItems = async () => {
    if (!selectedOrder) return;
    const validItems = clipboardItems.filter(i => i.found && i.typeId);
    if (validItems.length === 0) return;

    setClipboardAdding(true);
    try {
      const response = await api.post(`/sales-orders/${selectedOrder.id}/nodes`, {
        nodes: validItems.map(i => ({ typeId: i.typeId!, quantity: i.quantity })),
      });
      handleOrderUpdate(response.data);
      setShowClipboardDialog(false);
      setClipboardText('');
      setClipboardItems([]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add items');
    } finally {
      setClipboardAdding(false);
    }
  };

  const handleParseFitting = async () => {
    const parsed = parseFittingItems(fittingText);
    if (parsed.length === 0) { setFittingItems([]); return; }

    setFittingLookingUp(true);
    try {
      const response = await api.post('/sales-orders/types/lookup', { names: parsed.map(p => p.name) });
      const lookupMap = new Map<string, { typeId: number; typeName: string }>();
      for (const item of response.data.types) {
        if (item.typeId) lookupMap.set(item.name.toLowerCase(), { typeId: item.typeId, typeName: item.typeName });
      }
      setFittingItems(parsed.map(p => {
        const found = lookupMap.get(p.name.toLowerCase());
        return { ...p, typeId: found?.typeId, typeName: found?.typeName, found: !!found };
      }));
    } catch (err) {
      console.error('Failed to lookup types:', err);
    } finally {
      setFittingLookingUp(false);
    }
  };

  const handleAddFittingItems = async () => {
    if (!selectedOrder) return;
    const validItems = fittingItems.filter(i => i.found && i.typeId);
    if (validItems.length === 0) return;

    setFittingAdding(true);
    try {
      const response = await api.post(`/sales-orders/${selectedOrder.id}/nodes`, {
        nodes: validItems.map(i => ({ typeId: i.typeId!, quantity: i.quantity })),
      });
      handleOrderUpdate(response.data);
      setShowFittingDialog(false);
      setFittingText('');
      setFittingItems([]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add items');
    } finally {
      setFittingAdding(false);
    }
  };

  // ============================================================================
  // TREE RENDERING (BuildTree-style)
  // ============================================================================

  const toggleExpand = (nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  // Collect all node IDs recursively from the tree
  const collectAllNodeIds = useCallback((nodes: SalesOrderNode[]): string[] => {
    const ids: string[] = [];
    const collect = (nodeList: SalesOrderNode[]) => {
      for (const node of nodeList) {
        if (node.children.length > 0) {
          ids.push(node.id);
          collect(node.children);
        }
      }
    };
    collect(nodes);
    return ids;
  }, []);

  const handleExpandAll = useCallback(() => {
    if (!selectedOrder) return;
    const allIds = collectAllNodeIds(selectedOrder.nodes);
    setExpandedNodes(new Set(allIds));
  }, [selectedOrder, collectAllNodeIds]);

  const handleCollapseAll = useCallback(() => {
    setExpandedNodes(new Set());
  }, []);

  const renderTreeNode = (
    node: SalesOrderNode,
    depth: number = 0,
    isLastChild: boolean = false,
    ancestorIsLast: boolean[] = []
  ): React.ReactNode => {
    const isLevel1 = depth === 0; // Level 1 items (direct children of summary)
    const isSelected = selectedNodeId === node.id;
    const hasChildren = node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const isGhost = node.isGhostRef;
    const isShared = (node.sharedCount ?? 0) > 0 || isGhost;
    const totalSharedCount = (node.sharedCount ?? 0) + 1; // +1 for the defining node itself

    // Background color based on buy/build status
    // Ghost nodes get a different visual treatment
    const bgColor = isSelected
      ? 'bg-blue-600'
      : isGhost
        ? 'bg-cyan-900/20 hover:bg-cyan-800/30'
        : node.isBuild
          ? 'bg-green-900/30 hover:bg-green-800/40'
          : 'hover:bg-gray-700';

    return (
      <div key={node.id} className={isGhost ? 'opacity-60' : ''}>
        {/* Node row */}
        <div
          className={`group flex items-center gap-1 cursor-pointer transition-colors rounded ${bgColor}`}
          style={{ paddingRight: '8px' }}
          onClick={() => {
            // For ghost nodes, navigate to the defining node instead
            if (node.isGhostRef && node.definingNodeId) {
              setSelectedNodeId(node.definingNodeId);
              // Also expand the defining node if it's not already expanded
              setExpandedNodes(prev => {
                const next = new Set(prev);
                next.add(node.definingNodeId!);
                return next;
              });
            } else {
              setSelectedNodeId(node.id);
            }
          }}
        >
          {/* Ancestor vertical lines */}
          {ancestorIsLast.map((isAncestorLast, idx) => (
            <div key={idx} className="relative flex-shrink-0" style={{ width: '20px', height: '40px' }}>
              {!isAncestorLast && (
                <svg width="20" height="40" className="absolute inset-0">
                  <line x1="8" y1="0" x2="8" y2="40" stroke="#4B5563" strokeWidth="1" />
                </svg>
              )}
            </div>
          ))}

          {/* Tree connector for this node */}
          <TreeConnector
            isLast={isLastChild}
            hasChildren={hasChildren && !isGhost}
            isExpanded={isExpanded && !isGhost}
            onToggle={() => !isGhost && toggleExpand(node.id)}
          />

          {/* Icon with buy/build badge overlay */}
          <div className="relative flex-shrink-0">
            <img
              src={`https://images.evetech.net/types/${node.typeId}/icon?size=32`}
              alt={node.typeName}
              className="w-8 h-8 rounded"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            {/* Buy/Build indicator badge on top-right corner */}
            {isGhost ? (
              // Link icon for ghost (shared reference)
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-cyan-600 rounded-full flex items-center justify-center" title="Shared - references another node">
                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                </svg>
              </div>
            ) : node.isBuild ? (
              // Hammer icon for build
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-600 rounded-full flex items-center justify-center" title="Build">
                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
              </div>
            ) : (
              // Dollar icon for buy
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-600 rounded-full flex items-center justify-center" title="Buy">
                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>

          {/* Name and quantity */}
          <span className={`text-sm truncate ml-1 ${isSelected ? 'text-white font-medium' : isGhost ? 'text-cyan-300' : 'text-gray-300'}`}>
            {node.typeName}
            {isLevel1 && <span className="text-gray-500 ml-1">x{node.quantity.toLocaleString()}</span>}
          </span>

          {/* Shared indicator badge - show when this is a defining node with ghost refs */}
          {isShared && !isGhost && (
            <span
              className="px-1.5 py-0.5 text-[10px] bg-cyan-600 text-white rounded flex-shrink-0 flex items-center gap-0.5"
              title={`Shared component - used in ${totalSharedCount} locations`}
            >
              <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
              </svg>
              {totalSharedCount}
            </span>
          )}

          {/* Ghost reference badge */}
          {isGhost && (
            <span
              className="px-1 py-0.5 text-[10px] bg-cyan-700/50 text-cyan-300 rounded flex-shrink-0"
              title="This is a shared reference - settings are synced with the defining node"
            >
              ref
            </span>
          )}

          {/* Reaction badge */}
          {node.blueprintActivity === 'reaction' && (
            <span className="px-1 py-0.5 text-[10px] bg-purple-600 text-white rounded flex-shrink-0">R</span>
          )}

          {/* Remove button - only for level 1 items, visible on hover */}
          {isLevel1 && (
            <button
              onClick={(e) => { e.stopPropagation(); handleDeleteNode(node.id); }}
              className="ml-auto flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-600 hover:text-white text-gray-400 transition-all"
              title="Remove from order"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Children - Ghost nodes don't render their own children since they share the defining node's children */}
        {!isGhost && isExpanded && hasChildren && (() => {
          // Filter children if buildable-only filter is enabled (applies to level 2+ children)
          const childrenToRender = filterBuildableOnly && depth >= 0
            ? [...node.children].filter(child => child.buildableInfo !== null)
            : [...node.children];

          return childrenToRender.sort((a, b) => a.typeName.localeCompare(b.typeName)).map((child, index, sortedChildren) => {
            const childIsLast = index === sortedChildren.length - 1;
            return (
              <div key={child.id}>
                {renderTreeNode(
                  child,
                  depth + 1,
                  childIsLast,
                  [...ancestorIsLast, isLastChild]
                )}
              </div>
            );
          });
        })()}
      </div>
    );
  };

  // Get selected node from tree
  const findNodeById = (nodes: SalesOrderNode[], id: string): SalesOrderNode | null => {
    for (const node of nodes) {
      if (node.id === id) return node;
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
    return null;
  };

  // selectedNode is null when 'summary' is selected, otherwise find the actual node
  const selectedNode = selectedOrder && selectedNodeId && selectedNodeId !== 'summary'
    ? findNodeById(selectedOrder.nodes, selectedNodeId)
    : null;

  // Fetch owned blueprints when a node with usesOwnedBlueprint is selected
  useEffect(() => {
    if (selectedNode?.usesOwnedBlueprint && selectedNode?.blueprintId) {
      const fetchOwnedBlueprints = async () => {
        setLoadingOwnedBlueprints(true);
        try {
          const response = await api.get(`/sales-orders/owned-blueprints/${selectedNode.blueprintId}`);
          setOwnedBlueprints(response.data.blueprints);
        } catch (error) {
          console.error('Failed to fetch owned blueprints:', error);
          setOwnedBlueprints([]);
        } finally {
          setLoadingOwnedBlueprints(false);
        }
      };
      fetchOwnedBlueprints();
    } else {
      setOwnedBlueprints([]);
      setOwnedBlueprintsExpanded(false);
    }
  }, [selectedNode?.id, selectedNode?.usesOwnedBlueprint, selectedNode?.blueprintId]);

  // ============================================================================
  // FULFILLMENT SETTINGS HANDLERS
  // ============================================================================

  const handleOpenFulfillmentSettings = async () => {
    setShowFulfillmentSettings(true);
    setFulfillmentSettingsTab('systems');
    setLoadingFulfillmentSystems(true);
    setLoadingFulfillmentHangars(true);
    setFulfillmentSearchQuery('');
    setShowOnlyActiveSettings(false);
    try {
      const [systemsResponse, hangarsResponse] = await Promise.all([
        api.get('/fulfillment-settings/systems'),
        api.get('/fulfillment-settings/hangars'),
      ]);
      setFulfillmentSystems(systemsResponse.data.systems);
      setFulfillmentHangars(hangarsResponse.data.hangars);
    } catch (error) {
      console.error('Failed to fetch fulfillment settings:', error);
      setFulfillmentSystems([]);
    } finally {
      setLoadingFulfillmentSystems(false);
      setLoadingFulfillmentHangars(false);
    }
  };

  // Filter fulfillment systems based on search query and active filter
  const filteredFulfillmentSystems = useMemo(() => {
    return fulfillmentSystems.filter((system) => {
      const matchesSearch = fulfillmentSearchQuery === '' ||
        system.solarSystemName.toLowerCase().includes(fulfillmentSearchQuery.toLowerCase());
      const matchesActive = !showOnlyActiveSettings || system.salesOrderRelevant;
      return matchesSearch && matchesActive;
    });
  }, [fulfillmentSystems, fulfillmentSearchQuery, showOnlyActiveSettings]);

  const handleToggleFulfillmentSystem = (solarSystemId: number) => {
    setFulfillmentSystems((prev) =>
      prev.map((system) =>
        system.solarSystemId === solarSystemId
          ? { ...system, salesOrderRelevant: !system.salesOrderRelevant }
          : system
      )
    );
  };

  const handleToggleFulfillmentHangar = (hangarKey: keyof typeof fulfillmentHangars) => {
    setFulfillmentHangars((prev) => ({
      ...prev,
      [hangarKey]: !prev[hangarKey],
    }));
  };

  const handleSaveFulfillmentSettings = async () => {
    setSavingFulfillmentSettings(true);
    try {
      await Promise.all([
        api.post('/fulfillment-settings/systems', {
          systems: fulfillmentSystems.map((s) => ({
            solarSystemId: s.solarSystemId,
            salesOrderRelevant: s.salesOrderRelevant,
          })),
        }),
        api.post('/fulfillment-settings/hangars', {
          hangars: fulfillmentHangars,
        }),
      ]);
      setShowFulfillmentSettings(false);
    } catch (error) {
      console.error('Failed to save fulfillment settings:', error);
      setError('Failed to save fulfillment settings');
    } finally {
      setSavingFulfillmentSettings(false);
    }
  };

  // ============================================================================
  // RENDER: LOADING STATE
  // ============================================================================

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </Layout>
    );
  }

  // ============================================================================
  // RENDER: ORDER DETAIL VIEW (BuildTree-style)
  // ============================================================================

  if (orderNumber) {
    if (!selectedOrder) {
      return (
        <Layout>
          <div className="h-full flex flex-col p-6">
            <div className="flex items-center gap-2 mb-6">
              <Link to="/sales-orders" className="text-blue-400 hover:text-blue-300">Sales Orders</Link>
              <span className="text-gray-500">&gt;</span>
              <span className="text-gray-400">#{orderNumber}</span>
            </div>
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <p className="text-lg">Order not found</p>
            </div>
          </div>
        </Layout>
      );
    }

    // For accepted/delivered orders, use stored prices instead of live Janice prices
    const useStoredPrice = selectedOrder.status === 'accepted' || selectedOrder.status === 'delivered';
    const buildCost = calculateCompleteBuildCost(selectedOrder.nodes, buildCostPriceMode, structures, adjustedPrices, useStoredPrice);

    return (
      <Layout>
        {/* Build Complete Tree Progress Modal */}
        {buildingTree.active && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-gray-800 rounded-xl shadow-2xl border border-gray-700 p-8 max-w-md w-full mx-4">
              <div className="flex flex-col items-center text-center">
                {/* Animated Icon */}
                <div className="relative mb-6">
                  <div className="w-20 h-20 rounded-full border-4 border-blue-500/30 flex items-center justify-center">
                    <svg className="w-10 h-10 text-blue-500 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                    </svg>
                  </div>
                  {/* Spinning ring */}
                  <div className="absolute inset-0 w-20 h-20">
                    <svg className="w-full h-full animate-spin" viewBox="0 0 80 80">
                      <circle
                        cx="40"
                        cy="40"
                        r="36"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                        className="text-blue-500"
                        strokeDasharray="60 180"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </div>

                {/* Title */}
                <h2 className="text-xl font-bold text-white mb-2">Building Production Tree</h2>

                {/* Node name if available */}
                {buildingTree.nodeName && (
                  <p className="text-gray-400 text-sm mb-4 truncate max-w-full">
                    {buildingTree.nodeName}
                  </p>
                )}

                {/* Phase indicator */}
                <div className="w-full bg-gray-700 rounded-full h-2 mb-4">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${
                      buildingTree.phase === 'building'
                        ? 'w-1/2 bg-blue-500'
                        : 'w-full bg-green-500'
                    }`}
                  />
                </div>

                {/* Status text */}
                <p className="text-gray-300 text-sm">
                  {buildingTree.phase === 'building' && (
                    <>
                      <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse" />
                      Creating production nodes...
                    </>
                  )}
                  {buildingTree.phase === 'fetching-prices' && (
                    <>
                      <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse" />
                      Fetching market prices...
                    </>
                  )}
                </p>

                {/* Warning text */}
                <p className="text-yellow-500/70 text-xs mt-4">
                  Please wait, do not close this page
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="h-full flex flex-col">
          {/* Header Bar */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-gray-800">
            <div className="flex items-center gap-4">
              <Link to="/sales-orders" className="text-blue-400 hover:text-blue-300 text-lg font-semibold">
                Sales Orders
              </Link>
              <span className="text-gray-500">&gt;</span>
              <span className="text-white text-lg font-semibold">#{selectedOrder.orderNumber}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[selectedOrder.status]}`}>
                {STATUS_LABELS[selectedOrder.status]}
              </span>
              {selectedOrder.customer && (
                <span className="text-gray-400 text-sm">({selectedOrder.customer})</span>
              )}
            </div>
            <div className="flex items-center gap-4">
              {/* Total Cost (Build + Additional) */}
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-sm">Total Cost:</span>
                <span className="text-white font-mono font-medium">{formatISK(buildCost + (selectedOrder.additionalCost ?? 0))}</span>
                {selectedOrder.additionalCost ? (
                  <span className="text-xs text-gray-500">({formatISK(buildCost)} + {formatISK(selectedOrder.additionalCost)})</span>
                ) : null}
                <div className="inline-flex rounded border border-gray-600 overflow-hidden">
                  {(['buy', 'split', 'sell'] as const).map(mode => (
                    <button
                      key={mode}
                      onClick={() => setBuildCostPriceMode(mode)}
                      className={`px-2 py-0.5 text-xs ${
                        buildCostPriceMode === mode ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {mode.charAt(0).toUpperCase() + mode.slice(1)}
                    </button>
                  ))}
                </div>
                <button
                  onClick={handleRefreshPrices}
                  disabled={refreshingPrices || selectedOrder.nodes.length === 0}
                  className="p-1 text-gray-400 hover:text-white disabled:opacity-50"
                  title="Refresh prices"
                >
                  <svg className={`w-4 h-4 ${refreshingPrices ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Main Content - BuildTree-style layout */}
          <div className="flex-1 flex overflow-hidden">
            {/* Left Panel - Tree Navigation (20%) */}
            <div className="w-1/5 border-r border-gray-700 flex flex-col bg-gray-900">
              <div className="p-4 border-b border-gray-700">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white">Items ({selectedOrder.nodes.length})</h2>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={handleExpandAll}
                      className="p-1.5 rounded transition-colors bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-white"
                      title="Expand all"
                    >
                      <ChevronsUpDown className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleCollapseAll}
                      className="p-1.5 rounded transition-colors bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-white"
                      title="Collapse all"
                    >
                      <ChevronsDownUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setFilterBuildableOnly(!filterBuildableOnly)}
                      className={`p-1.5 rounded transition-colors ${
                        filterBuildableOnly
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                      }`}
                      title={filterBuildableOnly ? 'Show all materials' : 'Show buildable only'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {/* Sales Summary Root Node */}
                <div
                  className={`flex items-center gap-2 cursor-pointer transition-colors rounded p-2 ${
                    selectedNodeId === 'summary' ? 'bg-blue-600' : 'hover:bg-gray-700'
                  }`}
                  onClick={() => setSelectedNodeId('summary')}
                >
                  <div className="w-8 h-8 bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <span className={`text-sm font-medium ${selectedNodeId === 'summary' ? 'text-white' : 'text-gray-300'}`}>
                    Sales Summary
                  </span>
                </div>

                {/* Level 1 Items */}
                {selectedOrder.nodes.length === 0 ? (
                  <div className="text-gray-500 text-sm text-center py-4">No items yet</div>
                ) : (
                  [...selectedOrder.nodes].sort((a, b) => a.typeName.localeCompare(b.typeName)).map((node, index, sortedNodes) => {
                    const isLast = index === sortedNodes.length - 1;
                    return renderTreeNode(node, 0, isLast, []);
                  })
                )}
              </div>
              {/* Add buttons at bottom */}
              <div className="p-2 border-t border-gray-700 space-y-1">
                <button
                  onClick={() => setShowAddItemDialog(true)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Item
                </button>
                <button
                  onClick={() => setShowClipboardDialog(true)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Add from Clipboard
                </button>
                <button
                  onClick={() => setShowFittingDialog(true)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  Add from Fitting
                </button>
              </div>
            </div>

            {/* Right Panel - Details (80%) */}
            <div className="flex-1 bg-gray-800 overflow-auto">
              {!selectedNode ? (
                // Summary view when 'summary' is selected
                <div className="h-full flex flex-col">
                  {/* Summary Header */}
                  <div className="p-4 bg-gray-900 border-b border-gray-700">
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-gray-700 rounded-lg flex items-center justify-center">
                        <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h1 className="text-2xl font-bold text-white">Sales Summary</h1>
                        <p className="text-sm text-gray-400 mt-1">
                          {selectedOrder.nodes.length} item{selectedOrder.nodes.length !== 1 ? 's' : ''} in this order
                        </p>
                      </div>
                    </div>

                    {/* Order Details */}
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                      {/* Order Number */}
                      <div className="bg-gray-800 rounded-lg p-3">
                        <label className="block text-xs text-gray-400 mb-1">Order #</label>
                        <p className="text-white font-mono font-medium">{selectedOrder.orderNumber}</p>
                      </div>

                      {/* Description */}
                      <div className="bg-gray-800 rounded-lg p-3">
                        <label className="block text-xs text-gray-400 mb-1">Description</label>
                        <input
                          type="text"
                          value={localDescription}
                          onChange={(e) => setLocalDescription(e.target.value)}
                          onBlur={() => {
                            const newValue = localDescription || null;
                            if (newValue !== selectedOrder.description) {
                              handleUpdateSalesOrder({ description: newValue });
                            }
                          }}
                          placeholder="Order description"
                          className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>

                      {/* Status */}
                      <div className="bg-gray-800 rounded-lg p-3">
                        <label className="block text-xs text-gray-400 mb-1">Status</label>
                        <select
                          value={selectedOrder.status}
                          onChange={(e) => handleUpdateSalesOrder({ status: e.target.value })}
                          className={`w-full px-2 py-1 rounded text-sm font-medium border-0 cursor-pointer ${
                            STATUS_COLORS[selectedOrder.status] || 'bg-gray-600 text-white'
                          }`}
                        >
                          <option value="draft">Draft</option>
                          <option value="offered">Offered</option>
                          <option value="accepted">Accepted</option>
                          <option value="delivered">Delivered</option>
                        </select>
                      </div>

                      {/* Customer */}
                      <div className="bg-gray-800 rounded-lg p-3">
                        <label className="block text-xs text-gray-400 mb-1">Customer</label>
                        <input
                          type="text"
                          value={selectedOrder.customer || ''}
                          onChange={(e) => handleUpdateSalesOrder({ customer: e.target.value })}
                          placeholder="Enter customer name"
                          className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>

                      {/* Price Offered */}
                      <div className="bg-gray-800 rounded-lg p-3">
                        <label className="block text-xs text-gray-400 mb-1">Price Offered</label>
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={localPriceOffered ? parseFloat(localPriceOffered.replace(/,/g, '')).toLocaleString() : ''}
                            onChange={(e) => {
                              // Remove commas and non-numeric characters except decimal point
                              const raw = e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '');
                              setLocalPriceOffered(raw);
                            }}
                            onBlur={() => {
                              const raw = localPriceOffered.replace(/,/g, '');
                              const newValue = raw ? parseFloat(raw) : null;
                              if (newValue !== selectedOrder.priceOffered) {
                                handleUpdateSalesOrder({ priceOffered: newValue });
                              }
                            }}
                            placeholder="0"
                            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-500 text-right"
                          />
                          <span className="text-gray-400 text-xs">ISK</span>
                        </div>
                      </div>

                      {/* Price Accepted */}
                      <div className="bg-gray-800 rounded-lg p-3">
                        <label className="block text-xs text-gray-400 mb-1">Price Accepted</label>
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={localPriceAccepted ? parseFloat(localPriceAccepted.replace(/,/g, '')).toLocaleString() : ''}
                            onChange={(e) => {
                              // Remove commas and non-numeric characters except decimal point
                              const raw = e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '');
                              setLocalPriceAccepted(raw);
                            }}
                            onBlur={() => {
                              const raw = localPriceAccepted.replace(/,/g, '');
                              const newValue = raw ? parseFloat(raw) : null;
                              if (newValue !== selectedOrder.priceAccepted) {
                                handleUpdateSalesOrder({ priceAccepted: newValue });
                              }
                            }}
                            placeholder="0"
                            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-500 text-right"
                          />
                          <span className="text-gray-400 text-xs">ISK</span>
                        </div>
                      </div>

                      {/* Additional Cost */}
                      <div className="bg-gray-800 rounded-lg p-3">
                        <label className="block text-xs text-gray-400 mb-1">Additional Cost</label>
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={localAdditionalCost ? parseFloat(localAdditionalCost.replace(/,/g, '')).toLocaleString() : ''}
                            onChange={(e) => {
                              // Remove commas and non-numeric characters except decimal point
                              const raw = e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '');
                              setLocalAdditionalCost(raw);
                            }}
                            onBlur={() => {
                              const raw = localAdditionalCost.replace(/,/g, '');
                              const newValue = raw ? parseFloat(raw) : null;
                              if (newValue !== selectedOrder.additionalCost) {
                                handleUpdateSalesOrder({ additionalCost: newValue });
                              }
                            }}
                            placeholder="0"
                            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-500 text-right"
                          />
                          <span className="text-gray-400 text-xs">ISK</span>
                        </div>
                      </div>

                      {/* Created Date */}
                      <div className="bg-gray-800 rounded-lg p-3">
                        <label className="block text-xs text-gray-400 mb-1">Created</label>
                        <p className="text-white text-sm">{new Date(selectedOrder.createdAt).toLocaleDateString()}</p>
                      </div>

                      {/* Margin Offered */}
                      <div className="bg-gray-800 rounded-lg p-3">
                        <label className="block text-xs text-gray-400 mb-1">Margin Offered</label>
                        {(() => {
                          const priceOffered = localPriceOffered ? parseFloat(localPriceOffered) : null;
                          const additionalCost = localAdditionalCost ? parseFloat(localAdditionalCost) : 0;
                          const totalCost = buildCost + additionalCost;
                          const margin = priceOffered && totalCost > 0 ? ((priceOffered - totalCost) / totalCost) : null;
                          return (
                            <p className={`font-mono text-sm ${
                              margin !== null ? (margin >= 0 ? 'text-green-400' : 'text-red-400') : 'text-gray-500'
                            }`}>
                              {margin !== null ? `${(margin * 100).toFixed(1)}%` : '-'}
                            </p>
                          );
                        })()}
                      </div>

                      {/* Margin Final */}
                      <div className="bg-gray-800 rounded-lg p-3">
                        <label className="block text-xs text-gray-400 mb-1">Margin Final</label>
                        {(() => {
                          const priceAccepted = localPriceAccepted ? parseFloat(localPriceAccepted) : null;
                          const additionalCost = localAdditionalCost ? parseFloat(localAdditionalCost) : 0;
                          const totalCost = buildCost + additionalCost;
                          const margin = priceAccepted && totalCost > 0 ? ((priceAccepted - totalCost) / totalCost) : null;
                          return (
                            <p className={`font-mono text-sm ${
                              margin !== null ? (margin >= 0 ? 'text-green-400' : 'text-red-400') : 'text-gray-500'
                            }`}>
                              {margin !== null ? `${(margin * 100).toFixed(1)}%` : '-'}
                            </p>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 p-6 overflow-auto">
                    {selectedOrder.nodes.length === 0 ? (
                      <div className="h-full flex items-center justify-center">
                        <div className="text-center text-gray-500">
                          <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          <p className="text-lg">No items in this order</p>
                          <p className="text-sm mt-2">Click "Add item" to get started</p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {/* Production Defaults */}
                        <div className="bg-gray-700 rounded-lg p-4">
                          <h3 className="text-md font-semibold text-white mb-4">Production Defaults</h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                            {/* Engineering Complex */}
                            <div>
                              <label className="block text-sm text-gray-400 mb-1">Engineering Complex (Manufacturing)</label>
                              <select
                                value={defaultEngineeringComplex || ''}
                                onChange={(e) => handleUpdateSalesOrder({ defaultEngineeringComplex: e.target.value || null })}
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">No Structure (NPC Station)</option>
                                {structures
                                  .filter(s => [35825, 35826, 35827].includes(s.structureTypeId)) // Raitaru, Azbel, Sotiyo
                                  .map((structure) => (
                                    <option key={structure.id} value={structure.id}>
                                      {structure.solarSystem.nameEn} - {structure.name}
                                    </option>
                                  ))}
                              </select>
                            </div>

                            {/* Refinery */}
                            <div>
                              <label className="block text-sm text-gray-400 mb-1">Refinery (Reactions)</label>
                              <select
                                value={defaultRefinery || ''}
                                onChange={(e) => handleUpdateSalesOrder({ defaultRefinery: e.target.value || null })}
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">No Structure (NPC Station)</option>
                                {structures
                                  .filter(s => [35835, 35836].includes(s.structureTypeId)) // Athanor, Tatara
                                  .map((structure) => (
                                    <option key={structure.id} value={structure.id}>
                                      {structure.solarSystem.nameEn} - {structure.name}
                                    </option>
                                  ))}
                              </select>
                            </div>

                            {/* Apply Button */}
                            <div>
                              <button
                                onClick={handleApplyProductionDefaults}
                                disabled={applyingDefaults || (!defaultEngineeringComplex && !defaultRefinery)}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
                              >
                                {applyingDefaults ? (
                                  <>
                                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Applying...
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Apply to All Builds
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Sets the default structure for all current and future build items. Manufacturing uses the Engineering Complex, Reactions use the Refinery.
                          </p>
                        </div>

                        {/* Level 1 Items */}
                        <div className="flex items-center justify-between">
                          <h2 className="text-lg font-semibold text-white">Level 1 Items</h2>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={handleBuildAllTrees}
                              disabled={buildingAllTrees.active || selectedOrder.nodes.length === 0}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-sm text-white font-medium rounded-lg transition-colors"
                            >
                              {buildingAllTrees.active ? (
                                <>
                                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  {buildingAllTrees.phase === 'building' ? 'Building...' : 'Fetching Prices...'}
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                  </svg>
                                  Build All Trees
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => {
                                // Initialize edit quantities with current values
                                const quantities: Record<string, number> = {};
                                selectedOrder.nodes.forEach(node => {
                                  quantities[node.id] = node.quantity;
                                });
                                setEditQuantities(quantities);
                                setMultiplyValue(1);
                                setDivideValue(1);
                                setShowEditQuantitiesModal(true);
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-sm text-white font-medium rounded-lg transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit
                            </button>
                          </div>
                        </div>
                        {/* Items table */}
                        <div className="bg-gray-700 rounded-lg overflow-hidden">
                          <table className="w-full">
                            <thead className="bg-gray-600">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Item</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Qty</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">In Stock</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">In Prod.</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">In Proc.</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Unit Price</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Buy</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Saved</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Total</th>
                                <th className="px-4 py-2 text-center text-xs font-medium text-gray-400 uppercase">Mode</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-600">
                              {[...selectedOrder.nodes].sort((a, b) => a.typeName.localeCompare(b.typeName)).map(node => {
                                const nodeFulfillment = fulfillmentData[node.id] ?? { inStock: 0, inProduction: 0, inProcurement: 0 };
                                const totalAvailable = nodeFulfillment.inStock + nodeFulfillment.inProduction + nodeFulfillment.inProcurement;
                                const isFullyInStock = nodeFulfillment.inStock >= node.quantity;
                                const isCoveredByTotal = totalAvailable >= node.quantity;
                                const rowBgClass = isFullyInStock
                                  ? 'bg-green-900/20 hover:bg-green-900/30'
                                  : isCoveredByTotal
                                    ? 'bg-yellow-900/20 hover:bg-yellow-900/30'
                                    : 'hover:bg-gray-650';
                                return (
                                  <tr key={node.id} className={rowBgClass}>
                                    <td className="px-4 py-2">
                                      <div className="flex items-center gap-2">
                                        <img
                                          src={`https://images.evetech.net/types/${node.typeId}/icon?size=32`}
                                          alt={node.typeName}
                                          className="w-6 h-6 rounded"
                                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        />
                                        <span className="text-white">{node.typeName}</span>
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 text-right font-mono text-white">
                                      {node.quantity.toLocaleString()}
                                    </td>
                                    <td className={`px-4 py-2 text-right font-mono ${nodeFulfillment.inStock >= node.quantity ? 'text-green-400' : nodeFulfillment.inStock > 0 ? 'text-yellow-400' : 'text-gray-500'}`}>
                                      {nodeFulfillment.inStock.toLocaleString()}
                                    </td>
                                    <td className={`px-4 py-2 text-right font-mono ${nodeFulfillment.inProduction > 0 ? 'text-blue-400' : 'text-gray-500'}`}>
                                      {nodeFulfillment.inProduction.toLocaleString()}
                                    </td>
                                    <td className="px-4 py-2 text-right font-mono text-gray-500">
                                      {nodeFulfillment.inProcurement.toLocaleString()}
                                    </td>
                                    <td className={`px-4 py-2 text-right font-mono ${node.customPrice !== null ? 'text-yellow-400' : 'text-gray-300'}`}>
                                      {formatISK(getEffectivePrice(node, buildCostPriceMode, useStoredPrice))}
                                      {node.customPrice !== null && <span className="ml-1 text-yellow-500">*</span>}
                                    </td>
                                    <td className="px-4 py-2 text-right font-mono text-yellow-400">
                                      {formatISK(getEffectivePrice(node, buildCostPriceMode, useStoredPrice) * node.quantity)}
                                    </td>
                                    <td className="px-4 py-2 text-right font-mono">
                                      {(() => {
                                        const buyPrice = getEffectivePrice(node, buildCostPriceMode, useStoredPrice) * node.quantity;
                                        const totalPrice = getNodeProportionalCost(node, buildCostPriceMode, structures, adjustedPrices, useStoredPrice);
                                        const diff = totalPrice - buyPrice;
                                        const savedPercent = buyPrice > 0 ? (diff / buyPrice * 100) : 0;
                                        const isNegative = diff < 0;
                                        const sign = diff >= 0 ? '+' : '-';
                                        return (
                                          <span className={isNegative ? 'text-green-400' : diff > 0 ? 'text-red-400' : 'text-gray-400'}>
                                            {sign}{Math.abs(savedPercent).toFixed(1)}% ({sign}{formatISK(Math.abs(diff))})
                                          </span>
                                        );
                                      })()}
                                    </td>
                                    <td className="px-4 py-2 text-right font-mono text-white font-medium">
                                      {formatISK(getNodeProportionalCost(node, buildCostPriceMode, structures, adjustedPrices, useStoredPrice))}
                                    </td>
                                    <td className="px-4 py-2 text-center">
                                      <span className={`px-2 py-0.5 rounded text-xs ${
                                        node.isBuild ? 'bg-green-600/30 text-green-400' : 'bg-yellow-600/30 text-yellow-400'
                                      }`}>
                                        {node.isBuild ? 'Build' : 'Buy'}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot className="bg-gray-600">
                              <tr>
                                <td colSpan={6} className="px-4 py-2 text-right text-sm font-medium text-gray-300">
                                  Totals:
                                </td>
                                <td className="px-4 py-2 text-right font-mono text-yellow-400 font-bold">
                                  {formatISK(selectedOrder.nodes.reduce((sum, node) => sum + getEffectivePrice(node, buildCostPriceMode, useStoredPrice) * node.quantity, 0))}
                                </td>
                                <td className="px-4 py-2 text-right font-mono font-bold">
                                  {(() => {
                                    const totalBuy = selectedOrder.nodes.reduce((sum, node) => sum + getEffectivePrice(node, buildCostPriceMode, useStoredPrice) * node.quantity, 0);
                                    const diff = buildCost - totalBuy;
                                    const savedPercent = totalBuy > 0 ? (diff / totalBuy * 100) : 0;
                                    const isNegative = diff < 0;
                                    const sign = diff >= 0 ? '+' : '-';
                                    return (
                                      <span className={isNegative ? 'text-green-400' : diff > 0 ? 'text-red-400' : 'text-gray-400'}>
                                        {sign}{Math.abs(savedPercent).toFixed(1)}% ({sign}{formatISK(Math.abs(diff))})
                                      </span>
                                    );
                                  })()}
                                </td>
                                <td className="px-4 py-2 text-right font-mono text-white font-bold">
                                  {formatISK(buildCost)}
                                </td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // Node detail view - BuildTree style
                <div className="h-full flex flex-col">
                  {/* Header */}
                  <div className="p-6 border-b border-gray-700 bg-gray-900">
                    <div className="flex items-start gap-6">
                      {/* Left: Item Info */}
                      <div className="flex items-center space-x-4">
                        <img
                          src={`https://images.evetech.net/types/${selectedNode.typeId}/icon?size=64`}
                          alt={selectedNode.typeName}
                          className={`w-16 h-16 rounded-lg ${selectedNode.isGhostRef ? 'opacity-60' : ''}`}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <div>
                          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                            {selectedNode.typeName}
                            <span className="text-sm text-gray-400">({selectedNode.typeId})</span>
                            {selectedNode.blueprintActivity === 'reaction' && (
                              <span className="px-2 py-0.5 text-xs bg-purple-600 text-white rounded">Reaction</span>
                            )}
                            {/* Ghost reference badge */}
                            {selectedNode.isGhostRef && (
                              <span className="px-2 py-0.5 text-xs bg-cyan-600 text-white rounded flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                                </svg>
                                Shared Reference
                              </span>
                            )}
                            {/* Shared component badge (for defining nodes) */}
                            {!selectedNode.isGhostRef && (selectedNode.sharedCount ?? 0) > 0 && (
                              <span className="px-2 py-0.5 text-xs bg-cyan-600 text-white rounded flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                                </svg>
                                Shared ({(selectedNode.sharedCount ?? 0) + 1} locations)
                              </span>
                            )}
                          </h1>
                          <p className="text-sm text-gray-400">
                            Required: <span className="text-white font-mono">{selectedNode.quantity.toLocaleString()}</span>
                            {selectedNode.buildableInfo && (
                              <>
                                <span className="mx-2">|</span>
                                Output per run: <span className="text-white font-mono">{selectedNode.buildableInfo.outputQuantity}</span>
                                <span className="mx-2">|</span>
                                Runs needed: <span className="text-yellow-400 font-mono">{Math.ceil(selectedNode.quantity / selectedNode.buildableInfo.outputQuantity)}</span>
                              </>
                            )}
                          </p>
                          {/* Ghost reference info message */}
                          {selectedNode.isGhostRef && (
                            <p className="text-sm text-cyan-400 mt-1">
                              This is a shared reference. Settings are synced with the defining node.
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Right: Controls */}
                      <div className="flex flex-col gap-3 ml-auto items-end">
                        {/* Buy/Build Toggle - always show for buildable items */}
                        {selectedNode.buildableInfo ? (
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-400 font-medium">Mode:</span>
                            <div className="inline-flex rounded-lg border border-gray-700 overflow-hidden">
                              <button
                                onClick={() => handleBuy(selectedNode.id)}
                                disabled={nodeLoading === selectedNode.id}
                                className={`px-4 py-2 text-sm font-medium transition-colors ${
                                  !selectedNode.isBuild
                                    ? 'bg-yellow-600 text-white'
                                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                }`}
                              >
                                Buy
                              </button>
                              <button
                                onClick={() => handleBuild(selectedNode.id)}
                                disabled={nodeLoading === selectedNode.id}
                                className={`px-4 py-2 text-sm font-medium transition-colors border-l border-gray-700 ${
                                  selectedNode.isBuild
                                    ? 'bg-green-600 text-white'
                                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                }`}
                              >
                                Build
                              </button>
                            </div>
                            {nodeLoading === selectedNode.id && (
                              <svg className="w-5 h-5 animate-spin text-blue-400" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-400">Not buildable - will be purchased</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Content area */}
                  <div className="flex-1 p-6 overflow-auto">
                    {selectedNode.isBuild && selectedNode.children.length > 0 ? (
                      // BUILD MODE - Show materials table like BuildTree
                      (() => {
                        const selectedStructure = structures.find(s => s.id === selectedNode.structureId) ?? null;
                        const isReaction = selectedNode.blueprintActivity === 'reaction';
                        const runsNeeded = selectedNode.buildableInfo
                          ? Math.ceil(selectedNode.quantity / selectedNode.buildableInfo.outputQuantity)
                          : 1;

                        // Child quantities are already ME/structure/rig adjusted by the backend
                        // Calculate EIV using stored quantities (already adjusted)
                        const eiv = selectedNode.children.reduce((sum, child) => {
                          const adjPrice = adjustedPrices[child.typeId] ?? 0;
                          return sum + (adjPrice * child.quantity);
                        }, 0);

                        const systemCostIndex = isReaction
                          ? (selectedStructure?.solarSystem.costIndices?.reaction ?? 0)
                          : (selectedStructure?.solarSystem.costIndices?.manufacturing ?? 0);

                        const systemCost = eiv * systemCostIndex;
                        const structureRoleBonus = systemCost * getStructureRoleBonus(selectedStructure?.structureType?.id ?? null);
                        const jobGrossCost = systemCost - structureRoleBonus;
                        const sccSurcharge = eiv * 0.04;
                        const facilityTax = eiv * (selectedStructure?.facilityTax ?? 0);
                        const taxes = facilityTax + sccSurcharge;
                        const totalJobCost = jobGrossCost + taxes;

                        // Complete build cost uses the recursive function which includes nested job costs
                        const completeBuildCost = getNodeCompleteCost(selectedNode, buildCostPriceMode, structures, adjustedPrices, useStoredPrice);
                        // Calculate actual output (runs * output per run) for cost per item
                        const actualOutput = runsNeeded * (selectedNode.buildableInfo?.outputQuantity ?? 1);
                        const costPerItem = completeBuildCost / actualOutput;

                        return (
                          <div className="space-y-6">
                            {/* Build Settings Row */}
                            <div className="bg-gray-900 rounded-lg border border-gray-700 p-4">
                              <div className="flex flex-wrap items-center gap-6">
                                {/* ME Slider (not for reactions) */}
                                {!isReaction && (
                                  <div className="flex items-center gap-2">
                                    <label className="text-sm text-gray-400 font-medium">ME:</label>
                                    <input
                                      type="range"
                                      min="0"
                                      max="10"
                                      value={localME ?? selectedNode.materialEfficiency}
                                      onChange={(e) => setLocalME(parseInt(e.target.value))}
                                      onMouseUp={() => {
                                        if (localME !== null && localME !== selectedNode.materialEfficiency) {
                                          handleUpdateNodeSettings(selectedNode.id, { materialEfficiency: localME });
                                        }
                                        setLocalME(null);
                                      }}
                                      onTouchEnd={() => {
                                        if (localME !== null && localME !== selectedNode.materialEfficiency) {
                                          handleUpdateNodeSettings(selectedNode.id, { materialEfficiency: localME });
                                        }
                                        setLocalME(null);
                                      }}
                                      className="w-20 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                                    />
                                    <span className="text-sm font-mono text-white w-6 text-center">{localME ?? selectedNode.materialEfficiency}</span>
                                  </div>
                                )}

                                {/* TE Slider */}
                                <div className="flex items-center gap-2">
                                  <label className="text-sm text-gray-400 font-medium">TE:</label>
                                  <input
                                    type="range"
                                    min="0"
                                    max="20"
                                    step="2"
                                    value={localTE ?? selectedNode.timeEfficiency}
                                    onChange={(e) => setLocalTE(parseInt(e.target.value))}
                                    onMouseUp={() => {
                                      if (localTE !== null && localTE !== selectedNode.timeEfficiency) {
                                        handleUpdateNodeSettings(selectedNode.id, { timeEfficiency: localTE });
                                      }
                                      setLocalTE(null);
                                    }}
                                    onTouchEnd={() => {
                                      if (localTE !== null && localTE !== selectedNode.timeEfficiency) {
                                        handleUpdateNodeSettings(selectedNode.id, { timeEfficiency: localTE });
                                      }
                                      setLocalTE(null);
                                    }}
                                    className="w-20 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                                  />
                                  <span className="text-sm font-mono text-white w-6 text-center">{localTE ?? selectedNode.timeEfficiency}</span>
                                </div>

                                {/* Structure Selector */}
                                <div className="flex items-center gap-2 flex-1">
                                  <label className="text-sm text-gray-400 font-medium">Structure:</label>
                                  <select
                                    value={selectedNode.structureId || ''}
                                    onChange={(e) => handleUpdateNodeSettings(selectedNode.id, { structureId: e.target.value || null })}
                                    className="flex-1 max-w-xs px-3 py-1 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  >
                                    <option value="">No Structure (NPC Station)</option>
                                    {structures.map((structure) => (
                                      <option key={structure.id} value={structure.id}>
                                        {structure.solarSystem.nameEn} - {structure.name}
                                      </option>
                                    ))}
                                  </select>

                                  {/* Structure info */}
                                  {selectedStructure && (
                                    <div className="flex items-center gap-3 text-xs text-gray-400">
                                      <span>
                                        Sec: <span className={`font-medium ${
                                          (selectedStructure.solarSystem.securityStatus ?? 0) >= 0.5 ? 'text-green-400' :
                                          (selectedStructure.solarSystem.securityStatus ?? 0) > 0 ? 'text-yellow-400' : 'text-red-400'
                                        }`}>
                                          {(selectedStructure.solarSystem.securityStatus ?? 0).toFixed(2)}
                                        </span>
                                      </span>
                                      <span>
                                        Cost Index: <span className="text-white">
                                          {(((isReaction
                                            ? selectedStructure.solarSystem.costIndices?.reaction
                                            : selectedStructure.solarSystem.costIndices?.manufacturing) ?? 0) * 100).toFixed(2)}%
                                        </span>
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {/* Rig Selector */}
                                <div className="flex items-center gap-2">
                                  <label className="text-sm text-gray-400 font-medium">{isReaction ? 'Reaction' : 'Material'} Rig:</label>
                                  <div className="flex rounded-lg overflow-hidden border border-gray-600">
                                    {(['None', 'T1', 'T2'] as const).map((rig) => {
                                      const structureRig = isReaction
                                        ? selectedStructure?.reactionRig
                                        : selectedStructure?.materialRig;
                                      const effectiveRig = selectedNode.rigOverride ?? structureRig ?? 'None';
                                      const isSelected = effectiveRig === rig;
                                      const isDefault = structureRig === rig && !selectedNode.rigOverride;

                                      return (
                                        <button
                                          key={rig}
                                          onClick={() => {
                                            // If clicking the structure's default rig, clear the override
                                            if (structureRig === rig) {
                                              handleUpdateNodeSettings(selectedNode.id, { rigOverride: null });
                                            } else {
                                              handleUpdateNodeSettings(selectedNode.id, { rigOverride: rig });
                                            }
                                          }}
                                          className={`px-3 py-1 text-xs font-medium transition-colors ${
                                            isSelected
                                              ? 'bg-blue-600 text-white'
                                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                          } ${isDefault ? 'ring-1 ring-inset ring-blue-400' : ''}`}
                                        >
                                          {rig}{isDefault ? '*' : ''}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {(() => {
                                    const structureRig = isReaction
                                      ? selectedStructure?.reactionRig
                                      : selectedStructure?.materialRig;
                                    const effectiveRig = (selectedNode.rigOverride ?? structureRig ?? 'None') as 'None' | 'T1' | 'T2';
                                    const systemType = getSystemType(selectedStructure?.solarSystem.securityStatus ?? null);
                                    const rigBonus = getRigsMaterialModifier(effectiveRig, systemType);
                                    return rigBonus > 0 ? (
                                      <span className="text-xs text-green-400 ml-2">
                                        -{(rigBonus * 100).toFixed(2)}%
                                      </span>
                                    ) : null;
                                  })()}
                                </div>

                                {/* Update EIV Button */}
                                <button
                                  onClick={handleRefreshEIV}
                                  disabled={refreshingEIV}
                                  className="flex items-center gap-1.5 px-3 py-1 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-sm text-gray-300 rounded-lg transition-colors"
                                  title="Update Estimated Item Values from ESI"
                                >
                                  <svg className={`w-4 h-4 ${refreshingEIV ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                  {refreshingEIV ? 'Updating...' : 'Update EIV'}
                                </button>

                              </div>
                            </div>

                            {/* Build Price Summary with Job Costs */}
                            {(() => {
                              const outputQuantity = selectedNode.buildableInfo?.outputQuantity ?? 1;
                              const actualOutput = runsNeeded * outputQuantity;
                              const hasExtraOutput = actualOutput > selectedNode.quantity;
                              // Buy price for actual output (what we're producing)
                              const buyTotal = (selectedNode.priceBuy ?? 0) * actualOutput;
                              const splitTotal = (selectedNode.priceSplit ?? 0) * actualOutput;
                              const sellTotal = (selectedNode.priceSell ?? 0) * actualOutput;

                              return (
                                <div className="bg-gray-900 rounded-lg border border-gray-700 p-4">
                                  <div className="text-sm text-gray-400 mb-3">
                                    Build Cost vs Market Price ({selectedNode.typeName})
                                    {hasExtraOutput && (
                                      <span className="text-xs text-yellow-400 ml-2">
                                        Note: {runsNeeded} run{runsNeeded !== 1 ? 's' : ''} produces {actualOutput.toLocaleString()} items ({actualOutput - selectedNode.quantity} extra)
                                      </span>
                                    )}
                                  </div>

                                  {/* Per Item Prices */}
                                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Per Item</div>
                                  <div className="grid grid-cols-4 gap-4 mb-4">
                                    {/* Janice Buy */}
                                    <div className="text-center">
                                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Janice Buy</div>
                                      <div className="flex items-center justify-center gap-1">
                                        <span className="font-mono text-lg text-green-400">{formatISK(selectedNode.priceBuy)}</span>
                                        {selectedNode.priceBuy && costPerItem > 0 && (() => {
                                          const pct = ((selectedNode.priceBuy - costPerItem) / costPerItem * 100);
                                          const isPos = pct > 0;
                                          return (
                                            <>
                                              <PieChart percentage={Math.abs(pct)} isPositive={isPos} size={14} />
                                              <span className={`text-xs font-mono ${isPos ? 'text-green-400' : 'text-red-400'}`}>
                                                {isPos ? '+' : ''}{pct.toFixed(0)}%
                                              </span>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                    {/* Janice Split */}
                                    <div className="text-center">
                                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Janice Split</div>
                                      <div className="flex items-center justify-center gap-1">
                                        <span className="font-mono text-lg text-yellow-400">{formatISK(selectedNode.priceSplit)}</span>
                                        {selectedNode.priceSplit && costPerItem > 0 && (() => {
                                          const pct = ((selectedNode.priceSplit - costPerItem) / costPerItem * 100);
                                          const isPos = pct > 0;
                                          return (
                                            <>
                                              <PieChart percentage={Math.abs(pct)} isPositive={isPos} size={14} />
                                              <span className={`text-xs font-mono ${isPos ? 'text-green-400' : 'text-red-400'}`}>
                                                {isPos ? '+' : ''}{pct.toFixed(0)}%
                                              </span>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                    {/* Janice Sell */}
                                    <div className="text-center">
                                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Janice Sell</div>
                                      <div className="flex items-center justify-center gap-1">
                                        <span className="font-mono text-lg text-red-400">{formatISK(selectedNode.priceSell)}</span>
                                        {selectedNode.priceSell && costPerItem > 0 && (() => {
                                          const pct = ((selectedNode.priceSell - costPerItem) / costPerItem * 100);
                                          const isPos = pct > 0;
                                          return (
                                            <>
                                              <PieChart percentage={Math.abs(pct)} isPositive={isPos} size={14} />
                                              <span className={`text-xs font-mono ${isPos ? 'text-green-400' : 'text-red-400'}`}>
                                                {isPos ? '+' : ''}{pct.toFixed(0)}%
                                              </span>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                    {/* Build Cost per Item */}
                                    <div className="text-center">
                                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Build Cost</div>
                                      <div className="font-mono text-lg text-blue-400">
                                        {formatISK(costPerItem)}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Total Prices */}
                                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Total ({actualOutput.toLocaleString()} items)</div>
                                  <div className="grid grid-cols-4 gap-4">
                                    {/* Buy Total */}
                                    <div className="text-center">
                                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Buy Total</div>
                                      <div className="flex items-center justify-center gap-1">
                                        <span className="font-mono text-lg text-green-400">{formatISK(buyTotal)}</span>
                                        {buyTotal > 0 && completeBuildCost > 0 && (() => {
                                          const pct = ((buyTotal - completeBuildCost) / completeBuildCost * 100);
                                          const isPos = pct > 0;
                                          return (
                                            <>
                                              <PieChart percentage={Math.abs(pct)} isPositive={isPos} size={14} />
                                              <span className={`text-xs font-mono ${isPos ? 'text-green-400' : 'text-red-400'}`}>
                                                {isPos ? '+' : ''}{pct.toFixed(0)}%
                                              </span>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                    {/* Split Total */}
                                    <div className="text-center">
                                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Split Total</div>
                                      <div className="flex items-center justify-center gap-1">
                                        <span className="font-mono text-lg text-yellow-400">{formatISK(splitTotal)}</span>
                                        {splitTotal > 0 && completeBuildCost > 0 && (() => {
                                          const pct = ((splitTotal - completeBuildCost) / completeBuildCost * 100);
                                          const isPos = pct > 0;
                                          return (
                                            <>
                                              <PieChart percentage={Math.abs(pct)} isPositive={isPos} size={14} />
                                              <span className={`text-xs font-mono ${isPos ? 'text-green-400' : 'text-red-400'}`}>
                                                {isPos ? '+' : ''}{pct.toFixed(0)}%
                                              </span>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                    {/* Sell Total */}
                                    <div className="text-center">
                                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Sell Total</div>
                                      <div className="flex items-center justify-center gap-1">
                                        <span className="font-mono text-lg text-red-400">{formatISK(sellTotal)}</span>
                                        {sellTotal > 0 && completeBuildCost > 0 && (() => {
                                          const pct = ((sellTotal - completeBuildCost) / completeBuildCost * 100);
                                          const isPos = pct > 0;
                                          return (
                                            <>
                                              <PieChart percentage={Math.abs(pct)} isPositive={isPos} size={14} />
                                              <span className={`text-xs font-mono ${isPos ? 'text-green-400' : 'text-red-400'}`}>
                                                {isPos ? '+' : ''}{pct.toFixed(0)}%
                                              </span>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                    {/* Total Build Cost */}
                                    <div className="text-center">
                                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Build Total</div>
                                      <div className="font-mono text-lg text-blue-400">
                                        {formatISK(completeBuildCost)}
                                      </div>
                                      {hasExtraOutput && (
                                        <div className="text-xs text-gray-500">
                                          for {actualOutput.toLocaleString()} items
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Job Costs Breakdown */}
                            {totalJobCost > 0 && (
                              <div className="bg-gray-900 rounded-lg border border-gray-700 p-4">
                                <div className="text-sm text-gray-400 mb-3">Job Costs ({runsNeeded} run{runsNeeded !== 1 ? 's' : ''})</div>
                                <div className="grid grid-cols-5 gap-4 text-sm">
                                  <div className="text-center">
                                    <div className="text-xs text-gray-500 uppercase mb-1">EIV</div>
                                    <div className="font-mono text-gray-300">{formatISK(eiv)}</div>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-xs text-gray-500 uppercase mb-1">System Cost</div>
                                    <div className="font-mono text-gray-300">{formatISK(jobGrossCost)}</div>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-xs text-gray-500 uppercase mb-1">SCC Surcharge</div>
                                    <div className="font-mono text-gray-300">{formatISK(sccSurcharge)}</div>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-xs text-gray-500 uppercase mb-1">Facility Tax</div>
                                    <div className="font-mono text-gray-300">{formatISK(facilityTax)}</div>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-xs text-gray-500 uppercase mb-1">Total Job Cost</div>
                                    <div className="font-mono text-yellow-400 font-medium">{formatISK(totalJobCost)}</div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Owned Blueprints Section (collapsible) */}
                            {selectedNode.usesOwnedBlueprint && ownedBlueprints.length > 0 && (
                              <div className="bg-gray-900 rounded-lg border border-green-800">
                                <button
                                  onClick={() => setOwnedBlueprintsExpanded(!ownedBlueprintsExpanded)}
                                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-800/50 transition-colors"
                                >
                                  <div className="flex items-center gap-2">
                                    <svg
                                      className={`w-4 h-4 text-gray-400 transition-transform ${ownedBlueprintsExpanded ? 'rotate-90' : ''}`}
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    <span className="text-sm font-medium text-green-400">Owned Blueprints</span>
                                    <span className="text-xs text-gray-500">({ownedBlueprints.length})</span>
                                  </div>
                                  <span className="text-xs text-gray-400">
                                    Using ME {selectedNode.materialEfficiency}% / TE {selectedNode.timeEfficiency}%
                                  </span>
                                </button>
                                {ownedBlueprintsExpanded && (
                                  <div className="border-t border-green-800 p-4">
                                    {loadingOwnedBlueprints ? (
                                      <div className="flex items-center justify-center py-4">
                                        <svg className="animate-spin h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                      </div>
                                    ) : (
                                      <div className="overflow-x-auto">
                                        <table className="w-full">
                                          <thead>
                                            <tr className="text-left text-xs text-gray-400 uppercase border-b border-gray-700">
                                              <th className="pb-2 pr-4">Owner</th>
                                              <th className="pb-2 pr-4">Type</th>
                                              <th className="pb-2 pr-4">System</th>
                                              <th className="pb-2 pr-4">Location</th>
                                              <th className="pb-2 pr-4 text-center">ME</th>
                                              <th className="pb-2 pr-4 text-center">TE</th>
                                              <th className="pb-2 text-center">Runs</th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y divide-gray-700">
                                            {ownedBlueprints.map((bp) => (
                                              <tr key={bp.itemId} className="text-sm">
                                                <td className="py-2 pr-4">
                                                  <div className="flex items-center gap-2">
                                                    <img
                                                      src={`https://images.evetech.net/characters/${bp.characterId}/portrait?size=32`}
                                                      alt={bp.characterName}
                                                      className="w-6 h-6 rounded"
                                                    />
                                                    <span className="text-white">{bp.characterName}</span>
                                                  </div>
                                                </td>
                                                <td className="py-2 pr-4">
                                                  {bp.isOriginal ? (
                                                    <span className="text-blue-400 bg-blue-900/30 px-2 py-0.5 rounded text-xs">BPO</span>
                                                  ) : (
                                                    <span className="text-orange-400 bg-orange-900/30 px-2 py-0.5 rounded text-xs">BPC</span>
                                                  )}
                                                </td>
                                                <td className="py-2 pr-4 text-yellow-400">
                                                  {bp.solarSystemName || '-'}
                                                </td>
                                                <td className="py-2 pr-4 text-gray-400 max-w-[200px] truncate" title={bp.locationName || bp.locationId}>
                                                  {bp.locationName || `ID: ${bp.locationId}`}
                                                </td>
                                                <td className="py-2 pr-4 text-center">
                                                  <span className={`font-mono ${bp.materialEfficiency >= 10 ? 'text-green-400' : 'text-white'}`}>
                                                    {bp.materialEfficiency}%
                                                  </span>
                                                </td>
                                                <td className="py-2 pr-4 text-center">
                                                  <span className={`font-mono ${bp.timeEfficiency >= 20 ? 'text-green-400' : 'text-white'}`}>
                                                    {bp.timeEfficiency}%
                                                  </span>
                                                </td>
                                                <td className="py-2 text-center text-white font-mono">
                                                  {bp.isOriginal ? '∞' : bp.runs}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Materials Header */}
                            <div className="flex items-center justify-between">
                              <h2 className="text-xl font-semibold text-white">
                                {isReaction ? 'Reaction Inputs' : 'Input Materials'}
                              </h2>
                              <div className="flex items-center gap-2">
                                {(() => {
                                  const buildableCount = selectedNode.children.filter(c => c.buildableInfo && !c.isBuild).length;
                                  return buildableCount > 0 ? (
                                    <>
                                      <button
                                        onClick={() => handleBuildAll(selectedNode.children)}
                                        disabled={buildingAll || buildingTree.active}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-sm text-white font-medium rounded-lg transition-colors"
                                        title={`Set ${buildableCount} buildable item${buildableCount !== 1 ? 's' : ''} to Build mode`}
                                      >
                                        {buildingAll ? (
                                          <>
                                            <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                            Building...
                                          </>
                                        ) : (
                                          <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                            </svg>
                                            Build All ({buildableCount})
                                          </>
                                        )}
                                      </button>
                                      <button
                                        onClick={() => handleBuildCompleteTree(selectedNode.id)}
                                        disabled={buildingAll || buildingTree.active}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-sm text-white font-medium rounded-lg transition-colors"
                                        title="Recursively build this node and all buildable descendants"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                                        </svg>
                                        Build Complete Tree
                                      </button>
                                    </>
                                  ) : null;
                                })()}
                              </div>
                            </div>

                        {/* Materials Table */}
                        <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
                          <table className="w-full">
                            <thead className="bg-gray-800">
                              <tr className="border-b border-gray-700">
                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider border-r border-gray-700 w-12">
                                  #
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider border-r border-gray-700">
                                  Material
                                </th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Quantity</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Price</th>
                                <th className="px-4 py-2 text-center text-xs font-medium text-gray-400 uppercase tracking-wider w-32">Build / Buy</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                              {[...selectedNode.children].sort((a, b) => a.typeName.localeCompare(b.typeName)).map((child, index) => {
                                // Child quantities are already ME/structure/rig adjusted by the backend
                                const childPrice = getEffectivePrice(child, buildCostPriceMode, useStoredPrice);

                                // For build children, calculate proportional cost (not full production cost)
                                // The child might produce more than the parent needs due to batch sizes
                                let childTotal: number;
                                if (child.isBuild) {
                                  const childCompleteCost = getNodeCompleteCost(child, buildCostPriceMode, structures, adjustedPrices, useStoredPrice);
                                  // Calculate what the child actually produces
                                  const childOutputPerRun = child.buildableInfo?.outputQuantity ?? 1;
                                  const childRunsNeeded = Math.ceil(child.quantity / childOutputPerRun);
                                  const childActualProduced = childRunsNeeded * childOutputPerRun;
                                  // Calculate unit cost and multiply by what parent actually needs
                                  const childUnitCost = childCompleteCost / childActualProduced;
                                  childTotal = childUnitCost * child.quantity;
                                } else {
                                  childTotal = childPrice * child.quantity;
                                }

                                // Calculate buy price for savings comparison
                                const buyPrice = getEffectivePrice(child, buildCostPriceMode, useStoredPrice);
                                const buyTotal = buyPrice * child.quantity;
                                const savings = buyTotal - childTotal;
                                const savingsPercent = buyTotal > 0 ? (savings / buyTotal) * 100 : 0;

                                return (
                                  <tr
                                    key={child.id}
                                    className="hover:bg-gray-800 transition-colors cursor-pointer"
                                    onClick={() => {
                                      // For ghost nodes, navigate to the defining node instead
                                      if (child.isGhostRef && child.definingNodeId) {
                                        setSelectedNodeId(child.definingNodeId);
                                        setExpandedNodes(prev => {
                                          const next = new Set(prev);
                                          next.add(child.definingNodeId!);
                                          return next;
                                        });
                                      } else {
                                        setSelectedNodeId(child.id);
                                      }
                                    }}
                                  >
                                    <td className="px-2 py-2 text-left text-sm font-mono text-gray-400 border-r border-gray-700">
                                      {index + 1}
                                    </td>
                                    <td className="px-4 py-2 border-r border-gray-700">
                                      <div className="flex items-center gap-3">
                                        <img
                                          src={`https://images.evetech.net/types/${child.typeId}/icon?size=32`}
                                          alt={child.typeName}
                                          className="w-8 h-8 rounded"
                                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        />
                                        <div>
                                          <div className="text-sm font-medium text-white">
                                            {child.typeName}
                                          </div>
                                          <div className="text-xs text-gray-500">
                                            ID: {child.typeId}
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 text-right text-sm font-mono text-white">
                                      {child.quantity.toLocaleString()}
                                    </td>
                                    <td className="px-4 py-2 text-right text-sm">
                                      <div className="flex flex-col items-end">
                                        <span className={`${child.isBuild ? 'text-green-400' : child.customPrice !== null ? 'text-yellow-400' : 'text-gray-300'}`}>
                                          {formatISK(childTotal)}
                                          {child.customPrice !== null && <span className="ml-1 text-yellow-500">*</span>}
                                        </span>
                                        {child.isBuild && buyTotal > 0 && (
                                          <span className={`text-xs font-mono ${savings > 0 ? 'text-green-400' : savings < 0 ? 'text-red-400' : 'text-gray-500'}`}>
                                            {savings > 0 ? '-' : '+'}{Math.abs(savingsPercent).toFixed(1)}% ({savings > 0 ? '-' : '+'}{formatISK(Math.abs(savings))})
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                                      {nodeLoading === child.id ? (
                                        <div className="flex justify-center">
                                          <svg className="animate-spin h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                          </svg>
                                        </div>
                                      ) : (
                                        <div className="inline-flex rounded-lg border border-gray-700 overflow-hidden">
                                          <button
                                            onClick={() => handleBuy(child.id)}
                                            className={`px-2 py-1 text-xs font-medium transition-colors ${
                                              !child.isBuild
                                                ? 'bg-yellow-600 text-white'
                                                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                            }`}
                                          >
                                            Buy
                                          </button>
                                          <button
                                            onClick={() => handleBuild(child.id)}
                                            disabled={!child.buildableInfo}
                                            className={`px-2 py-1 text-xs font-medium transition-colors border-l border-gray-700 ${
                                              !child.buildableInfo
                                                ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                                                : child.isBuild
                                                  ? 'bg-green-600 text-white'
                                                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                            }`}
                                            title={!child.buildableInfo ? 'No blueprint available' : (child.buildableInfo.activity === 'reaction' ? 'React this material' : 'Build this material')}
                                          >
                                            {child.buildableInfo?.activity === 'reaction' ? 'React' : 'Build'}
                                          </button>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot className="bg-gray-800">
                              <tr>
                                <td colSpan={3} className="px-4 py-3 text-right text-sm font-medium text-gray-300">
                                  Total Materials Cost:
                                </td>
                                <td className="px-4 py-3 text-right font-mono text-white font-bold">
                                  {formatISK(selectedNode.children.reduce((sum, c) => {
                                    // Child quantities are already ME/structure/rig adjusted by the backend
                                    if (c.isBuild) {
                                      // Calculate proportional cost (not full production cost)
                                      const childCompleteCost = getNodeCompleteCost(c, buildCostPriceMode, structures, adjustedPrices, useStoredPrice);
                                      const childOutputPerRun = c.buildableInfo?.outputQuantity ?? 1;
                                      const childRunsNeeded = Math.ceil(c.quantity / childOutputPerRun);
                                      const childActualProduced = childRunsNeeded * childOutputPerRun;
                                      const childUnitCost = childCompleteCost / childActualProduced;
                                      return sum + (childUnitCost * c.quantity);
                                    }
                                    return sum + (getEffectivePrice(c, buildCostPriceMode, useStoredPrice) * c.quantity);
                                  }, 0))}
                                </td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                          </div>
                        );
                      })()
                    ) : (
                      // BUY MODE - Show pricing info
                      <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-white">Pricing</h2>

                        {/* Price comparison */}
                        <div className="bg-gray-900 rounded-lg border border-gray-700 p-4">
                          <div className="text-sm text-gray-400 mb-3">Market Prices ({selectedNode.typeName})</div>
                          <div className="grid grid-cols-3 gap-4">
                            {/* Buy */}
                            <div className="text-center">
                              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Janice Buy</div>
                              <div className={`font-mono text-lg ${buildCostPriceMode === 'buy' && selectedNode.customPrice === null ? 'text-green-400 ring-2 ring-green-500 rounded px-2 py-1' : 'text-green-400'}`}>
                                {formatISK(selectedNode.priceBuy)}
                              </div>
                            </div>
                            {/* Split */}
                            <div className="text-center">
                              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Janice Split</div>
                              <div className={`font-mono text-lg ${buildCostPriceMode === 'split' && selectedNode.customPrice === null ? 'text-yellow-400 ring-2 ring-yellow-500 rounded px-2 py-1' : 'text-yellow-400'}`}>
                                {formatISK(selectedNode.priceSplit)}
                              </div>
                            </div>
                            {/* Sell */}
                            <div className="text-center">
                              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Janice Sell</div>
                              <div className={`font-mono text-lg ${buildCostPriceMode === 'sell' && selectedNode.customPrice === null ? 'text-red-400 ring-2 ring-red-500 rounded px-2 py-1' : 'text-red-400'}`}>
                                {formatISK(selectedNode.priceSell)}
                              </div>
                            </div>
                          </div>

                          {/* Custom price */}
                          <div className="mt-4 pt-4 border-t border-gray-700">
                            <div className="flex items-center justify-center gap-4">
                              <label className="text-sm text-gray-400">Custom Price:</label>
                              <input
                                type="number"
                                value={localCustomPrice}
                                onChange={(e) => setLocalCustomPrice(e.target.value)}
                                onBlur={() => {
                                  const newValue = localCustomPrice === '' ? null : parseFloat(localCustomPrice);
                                  if (newValue !== selectedNode.customPrice) {
                                    handleUpdateNodeCustomPrice(selectedNode.id, newValue);
                                  }
                                }}
                                placeholder="Use market price"
                                className={`w-40 px-3 py-2 bg-gray-800 border rounded text-center font-mono ${
                                  localCustomPrice !== '' ? 'border-yellow-500 text-yellow-400' : 'border-gray-600 text-white'
                                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                              />
                              {localCustomPrice !== '' && (
                                <button
                                  onClick={() => {
                                    setLocalCustomPrice('');
                                    handleUpdateNodeCustomPrice(selectedNode.id, null);
                                  }}
                                  className="text-xs text-gray-400 hover:text-white"
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Total */}
                          <div className="mt-4 pt-4 border-t border-gray-700 text-center">
                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                              Total Cost ({selectedNode.quantity.toLocaleString()} × {formatISK(getEffectivePrice(selectedNode, buildCostPriceMode, useStoredPrice))})
                            </div>
                            <div className="font-mono text-2xl text-white font-bold">
                              {formatISK(getNodeTotal(selectedNode, buildCostPriceMode, useStoredPrice))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Add Item Dialog */}
          {showAddItemDialog && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 w-full max-w-2xl max-h-[80vh] flex flex-col">
                <h2 className="text-xl font-bold text-white mb-4">Add Items</h2>
                <p className="text-gray-400 text-sm mb-4">Search for items (min 2 characters). Click items to select, then set quantities.</p>

                {/* Search Input */}
                <div className="mb-4">
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      value={itemSearchQuery}
                      onChange={(e) => setItemSearchQuery(e.target.value)}
                      placeholder="Search by item name (min 2 chars)..."
                      className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                    {itemSearchQuery && (
                      <button
                        onClick={() => { setItemSearchQuery(''); setItemSearchResults([]); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {itemSearching && <div className="mt-2 text-gray-400 text-sm">Searching...</div>}
                </div>

                {/* Search Results Table */}
                {itemSearchResults.length > 0 && (
                  <div className="mb-4 max-h-48 overflow-y-auto bg-gray-700 rounded-lg border border-gray-600">
                    <table className="w-full">
                      <thead className="bg-gray-600 sticky top-0">
                        <tr>
                          <th className="w-10 px-3 py-2"></th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">Item</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-600">
                        {itemSearchResults.map(type => (
                          <tr
                            key={type.id}
                            onClick={() => toggleItemSelection(type)}
                            className={`cursor-pointer hover:bg-gray-600 transition-colors ${selectedItems.has(type.id) ? 'bg-blue-900/40' : ''}`}
                          >
                            <td className="px-3 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={selectedItems.has(type.id)}
                                onChange={() => toggleItemSelection(type)}
                                className="w-4 h-4 rounded border-gray-500 bg-gray-600 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-800"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                {(() => { const iconUrl = getSearchItemIconUrl(type.id, type.name); return iconUrl && <img src={iconUrl} alt={type.name} className="w-6 h-6 rounded" onError={(e) => { e.currentTarget.style.display = 'none'; }} />; })()}
                                <span className="text-white">{type.name}</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Selected Items with Quantities */}
                {selectedItems.size > 0 && (
                  <div className="flex-1 overflow-y-auto mb-4">
                    <div className="text-sm font-medium text-gray-400 mb-2">Selected Items ({selectedItems.size})</div>
                    <div className="bg-gray-700 rounded-lg border border-gray-600">
                      <table className="w-full">
                        <thead className="bg-gray-600 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">Item</th>
                            <th className="w-32 px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">Quantity</th>
                            <th className="w-10 px-3 py-2"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-600">
                          {Array.from(selectedItems.values()).map(({ type, quantity }) => (
                            <tr key={type.id}>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  {(() => { const iconUrl = getSearchItemIconUrl(type.id, type.name); return iconUrl && <img src={iconUrl} alt={type.name} className="w-6 h-6 rounded" onError={(e) => { e.currentTarget.style.display = 'none'; }} />; })()}
                                  <span className="text-white">{type.name}</span>
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  min="1"
                                  value={quantity}
                                  onChange={(e) => updateItemQuantity(type.id, parseInt(e.target.value, 10) || 1)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-full px-2 py-1 bg-gray-600 border border-gray-500 rounded text-white text-right focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                              </td>
                              <td className="px-3 py-2 text-center">
                                <button
                                  onClick={() => toggleItemSelection(type)}
                                  className="text-gray-400 hover:text-red-400 transition-colors"
                                  title="Remove"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div className="flex justify-between items-center pt-2 border-t border-gray-700">
                  <div className="text-sm text-gray-400">
                    {selectedItems.size > 0 && <span>{selectedItems.size} item{selectedItems.size > 1 ? 's' : ''} selected</span>}
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => { setShowAddItemDialog(false); setItemSearchQuery(''); setItemSearchResults([]); setSelectedItems(new Map()); }} disabled={addingItem} className="px-4 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50">Cancel</button>
                    <button onClick={handleAddItems} disabled={selectedItems.size === 0 || addingItem} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors">
                      {addingItem && <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>}
                      Add {selectedItems.size > 0 ? selectedItems.size : ''} Item{selectedItems.size !== 1 ? 's' : ''}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Add from Clipboard Dialog */}
          {showClipboardDialog && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 w-full max-w-2xl max-h-[80vh] flex flex-col">
                <h2 className="text-xl font-bold text-white mb-4">Add from Clipboard</h2>
                <p className="text-gray-400 text-sm mb-4">Paste items from EVE Online. Format: "Item Name [tab] Quantity" per line.</p>
                <textarea
                  value={clipboardText}
                  onChange={(e) => setClipboardText(e.target.value)}
                  placeholder="Paste items here..."
                  rows={6}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm mb-4"
                />
                <button onClick={handleParseClipboard} disabled={!clipboardText.trim() || clipboardLookingUp} className="mb-4 flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors">
                  {clipboardLookingUp ? <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Looking up items...</> : 'Parse Items'}
                </button>
                {clipboardItems.length > 0 && (
                  <div className="flex-1 overflow-y-auto mb-4 bg-gray-700 rounded-lg border border-gray-600">
                    <table className="w-full">
                      <thead className="bg-gray-600 sticky top-0"><tr><th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Item</th><th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Qty</th><th className="px-4 py-2 text-center text-xs font-medium text-gray-400 uppercase">Status</th></tr></thead>
                      <tbody className="divide-y divide-gray-600">
                        {clipboardItems.map((item, idx) => (
                          <tr key={idx} className={item.found ? '' : 'opacity-50'}>
                            <td className="px-4 py-2"><div className="flex items-center gap-2">{item.found && item.typeId && <img src={`https://images.evetech.net/types/${item.typeId}/icon?size=32`} alt={item.typeName || item.name} className="w-6 h-6 rounded" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}<span className="text-white">{item.typeName || item.name}</span></div></td>
                            <td className="px-4 py-2 text-right font-mono text-white">{item.quantity.toLocaleString()}</td>
                            <td className="px-4 py-2 text-center">{item.found ? <span className="text-green-400">Found</span> : <span className="text-red-400">Not found</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-400">{clipboardItems.length > 0 && <span>{clipboardItems.filter(i => i.found).length} of {clipboardItems.length} items found</span>}</div>
                  <div className="flex gap-3">
                    <button onClick={() => { setShowClipboardDialog(false); setClipboardText(''); setClipboardItems([]); }} disabled={clipboardAdding} className="px-4 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50">Cancel</button>
                    <button onClick={handleAddClipboardItems} disabled={clipboardItems.filter(i => i.found).length === 0 || clipboardAdding} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors">
                      {clipboardAdding && <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>}
                      Add {clipboardItems.filter(i => i.found).length} Items
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Add from Fitting Dialog */}
          {showFittingDialog && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 w-full max-w-2xl max-h-[80vh] flex flex-col">
                <h2 className="text-xl font-bold text-white mb-4">Add from Fitting</h2>
                <p className="text-gray-400 text-sm mb-4">Paste an EVE Online fitting export. The ship type will be ignored.</p>
                <textarea
                  value={fittingText}
                  onChange={(e) => setFittingText(e.target.value)}
                  placeholder="Paste fitting here..."
                  rows={8}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm mb-4"
                />
                <button onClick={handleParseFitting} disabled={!fittingText.trim() || fittingLookingUp} className="mb-4 flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors">
                  {fittingLookingUp ? <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Looking up items...</> : 'Parse Fitting'}
                </button>
                {fittingItems.length > 0 && (
                  <div className="flex-1 overflow-y-auto mb-4 bg-gray-700 rounded-lg border border-gray-600">
                    <table className="w-full">
                      <thead className="bg-gray-600 sticky top-0"><tr><th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Item</th><th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Qty</th><th className="px-4 py-2 text-center text-xs font-medium text-gray-400 uppercase">Status</th></tr></thead>
                      <tbody className="divide-y divide-gray-600">
                        {fittingItems.map((item, idx) => (
                          <tr key={idx} className={item.found ? '' : 'opacity-50'}>
                            <td className="px-4 py-2"><div className="flex items-center gap-2">{item.found && item.typeId && <img src={`https://images.evetech.net/types/${item.typeId}/icon?size=32`} alt={item.typeName || item.name} className="w-6 h-6 rounded" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}<span className="text-white">{item.typeName || item.name}</span></div></td>
                            <td className="px-4 py-2 text-right font-mono text-white">{item.quantity.toLocaleString()}</td>
                            <td className="px-4 py-2 text-center">{item.found ? <span className="text-green-400">Found</span> : <span className="text-red-400">Not found</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-400">{fittingItems.length > 0 && <span>{fittingItems.filter(i => i.found).length} of {fittingItems.length} items found</span>}</div>
                  <div className="flex gap-3">
                    <button onClick={() => { setShowFittingDialog(false); setFittingText(''); setFittingItems([]); }} disabled={fittingAdding} className="px-4 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50">Cancel</button>
                    <button onClick={handleAddFittingItems} disabled={fittingItems.filter(i => i.found).length === 0 || fittingAdding} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors">
                      {fittingAdding && <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>}
                      Add {fittingItems.filter(i => i.found).length} Items
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Quantities Modal */}
          {showEditQuantitiesModal && selectedOrder && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 w-full max-w-2xl max-h-[80vh] flex flex-col">
                <h2 className="text-xl font-bold text-white mb-4">Edit Quantities</h2>

                {/* Multiply and Divide controls */}
                <div className="flex items-center gap-6 mb-4 p-3 bg-gray-700 rounded-lg">
                  {/* Multiply */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-400">Multiplier:</label>
                    <input
                      type="number"
                      min="1"
                      value={multiplyValue}
                      onChange={(e) => setMultiplyValue(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-20 px-3 py-1.5 bg-gray-600 border border-gray-500 rounded-lg text-white text-center font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => {
                        if (multiplyValue > 1) {
                          const newQuantities = { ...editQuantities };
                          Object.keys(newQuantities).forEach(id => {
                            newQuantities[id] = newQuantities[id] * multiplyValue;
                          });
                          setEditQuantities(newQuantities);
                          setMultiplyValue(1);
                        }
                      }}
                      disabled={multiplyValue <= 1}
                      className="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Multiply
                    </button>
                  </div>

                  {/* Divider */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-400">Divider:</label>
                    <input
                      type="number"
                      min="1"
                      value={divideValue}
                      onChange={(e) => setDivideValue(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-20 px-3 py-1.5 bg-gray-600 border border-gray-500 rounded-lg text-white text-center font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => {
                        if (divideValue > 1) {
                          const newQuantities = { ...editQuantities };
                          Object.keys(newQuantities).forEach(id => {
                            // Divide and round up to next whole number
                            newQuantities[id] = Math.ceil(newQuantities[id] / divideValue);
                          });
                          setEditQuantities(newQuantities);
                          setDivideValue(1);
                        }
                      }}
                      disabled={divideValue <= 1}
                      className="px-3 py-1.5 bg-orange-600 hover:bg-orange-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Divide
                    </button>
                  </div>
                </div>

                {/* Items list */}
                <div className="flex-1 overflow-y-auto mb-4 bg-gray-700 rounded-lg border border-gray-600">
                  <table className="w-full">
                    <thead className="bg-gray-600 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Item</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase w-32">Original</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase w-40">New Quantity</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-600">
                      {[...selectedOrder.nodes].sort((a, b) => a.typeName.localeCompare(b.typeName)).map(node => (
                        <tr key={node.id}>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <img
                                src={`https://images.evetech.net/types/${node.typeId}/icon?size=32`}
                                alt={node.typeName}
                                className="w-6 h-6 rounded"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                              <span className="text-white">{node.typeName}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-gray-400">
                            {node.quantity.toLocaleString()}
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="number"
                              min="1"
                              value={editQuantities[node.id] || 1}
                              onChange={(e) => {
                                const val = Math.max(1, parseInt(e.target.value) || 1);
                                setEditQuantities(prev => ({ ...prev, [node.id]: val }));
                              }}
                              className="w-full px-3 py-1.5 bg-gray-600 border border-gray-500 rounded-lg text-white text-right font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Footer buttons */}
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowEditQuantitiesModal(false);
                      setEditQuantities({});
                      setMultiplyValue(1);
                      setDivideValue(1);
                    }}
                    disabled={savingQuantities}
                    className="px-4 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!selectedOrder) return;
                      setSavingQuantities(true);
                      try {
                        // Update each node's quantity if it changed
                        const updates: Promise<any>[] = [];
                        for (const node of selectedOrder.nodes) {
                          const newQty = editQuantities[node.id];
                          if (newQty !== undefined && newQty !== node.quantity) {
                            console.log(`Updating node ${node.id} (${node.typeName}) from ${node.quantity} to ${newQty}`);
                            updates.push(
                              api.put(`/sales-orders/${selectedOrder.id}/nodes/${node.id}/quantity`, { quantity: newQty })
                            );
                          }
                        }
                        console.log(`Sending ${updates.length} quantity updates`);
                        if (updates.length > 0) {
                          await Promise.all(updates);
                        }
                        // Refresh all orders to get the updated data
                        const response = await api.get('/sales-orders');
                        const updatedOrders = response.data.salesOrders;
                        setSalesOrders(updatedOrders);
                        // Also update selectedOrder to reflect the changes
                        const updatedSelectedOrder = updatedOrders.find((o: SalesOrder) => o.id === selectedOrder.id);
                        if (updatedSelectedOrder) {
                          setSelectedOrder(updatedSelectedOrder);
                        }
                        setShowEditQuantitiesModal(false);
                        setEditQuantities({});
                        setMultiplyValue(1);
                        setDivideValue(1);
                      } catch (err) {
                        console.error('Failed to update quantities:', err);
                        setError('Failed to update quantities');
                      } finally {
                        setSavingQuantities(false);
                      }
                    }}
                    disabled={savingQuantities}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    {savingQuantities && (
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Layout>
    );
  }

  // ============================================================================
  // RENDER: ORDER LIST VIEW
  // ============================================================================

  return (
    <Layout>
      <div className="h-full flex flex-col p-6 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-white">Sales Orders</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={handleOpenFulfillmentSettings}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Fulfillment Settings
            </button>
            <button
              onClick={() => setShowCreateDialog(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Sales Order
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-900/30 border border-red-700 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-400">{error}</span>
              <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Sales Orders Table */}
        <div className="flex-1 bg-gray-800 rounded-lg border border-gray-700 overflow-hidden flex flex-col min-h-0">
          {salesOrders.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-lg mb-2">No sales orders yet</p>
                <p className="text-sm text-gray-500">Create your first sales order to get started</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-700 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Order #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Customer</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase">Items</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Build Cost</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Created</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {salesOrders.map(order => (
                    <tr key={order.id} className="hover:bg-gray-700 transition-colors">
                      <td className="px-4 py-3">
                        <Link to={`/sales-orders/${order.orderNumber}`} className="text-blue-400 hover:text-blue-300 font-mono font-medium">
                          #{order.orderNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-gray-300 max-w-[200px] truncate" title={order.description || ''}>
                        {order.description || <span className="text-gray-500 italic">-</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[order.status]}`}>
                          {STATUS_LABELS[order.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        {order.customer || <span className="text-gray-500 italic">No customer</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-700 text-gray-300 text-sm font-medium">
                          {order.nodes.length}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-gray-300">
                        {formatNumber(order.buildCost)}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {formatDate(order.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(order); }}
                          className="text-red-400 hover:text-red-300 transition-colors p-1"
                          title="Delete order"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create Dialog */}
        {showCreateDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 w-full max-w-md">
              <h2 className="text-xl font-bold text-white mb-4">New Sales Order</h2>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400 mb-2">Customer Name</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !creating) handleCreateOrder(); }}
                  placeholder="Enter customer name (optional)"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => { setShowCreateDialog(false); setCustomerName(''); }} disabled={creating} className="px-4 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50">Cancel</button>
                <button onClick={handleCreateOrder} disabled={creating} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg transition-colors">
                  {creating && <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>}
                  Create Order
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 w-full max-w-md">
              <h2 className="text-xl font-bold text-white mb-4">Delete Sales Order</h2>
              <p className="text-gray-300 mb-6">
                Are you sure you want to delete order <span className="font-mono text-white">#{showDeleteConfirm.orderNumber}</span>?
                {showDeleteConfirm.customer && <span> (Customer: {showDeleteConfirm.customer})</span>}
                <br /><span className="text-red-400 text-sm">This action cannot be undone.</span>
              </p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowDeleteConfirm(null)} disabled={deleting !== null} className="px-4 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50">Cancel</button>
                <button onClick={() => handleDeleteOrder(showDeleteConfirm)} disabled={deleting !== null} className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white rounded-lg transition-colors">
                  {deleting === showDeleteConfirm.id && <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>}
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Fulfillment Settings Modal */}
        {showFulfillmentSettings && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 w-full max-w-lg max-h-[80vh] flex flex-col">
              <h2 className="text-xl font-bold text-white mb-4">Fulfillment Settings</h2>

              {/* Tabs */}
              <div className="flex border-b border-gray-700 mb-4">
                <button
                  onClick={() => setFulfillmentSettingsTab('systems')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    fulfillmentSettingsTab === 'systems'
                      ? 'text-blue-400 border-b-2 border-blue-400'
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  Systems
                </button>
                <button
                  onClick={() => setFulfillmentSettingsTab('hangars')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    fulfillmentSettingsTab === 'hangars'
                      ? 'text-blue-400 border-b-2 border-blue-400'
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  Corporation Hangars
                </button>
              </div>

              {/* Systems Tab */}
              {fulfillmentSettingsTab === 'systems' && (
                <>
                  <p className="text-gray-400 text-sm mb-4">
                    Select which systems with assets should be considered when fulfilling sales orders.
                  </p>

                  {/* Search and Filter */}
                  <div className="flex gap-3 mb-4">
                    <div className="flex-1 relative">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        value={fulfillmentSearchQuery}
                        onChange={(e) => setFulfillmentSearchQuery(e.target.value)}
                        placeholder="Search systems..."
                        className="w-full pl-9 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                    <button
                      onClick={() => setShowOnlyActiveSettings(!showOnlyActiveSettings)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        showOnlyActiveSettings
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                      Active
                    </button>
                  </div>

                  {/* Systems list */}
                  <div className="flex-1 overflow-y-auto min-h-0 mb-4">
                    {loadingFulfillmentSystems ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      </div>
                    ) : fulfillmentSystems.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <p>No systems with assets found.</p>
                        <p className="text-sm mt-2">Sync your character assets first.</p>
                      </div>
                    ) : filteredFulfillmentSystems.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <p>No systems match your filter.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {filteredFulfillmentSystems.map((system) => (
                          <div
                            key={system.solarSystemId}
                            className="flex items-center justify-between p-3 bg-gray-700 rounded-lg"
                          >
                            <span className="text-white">{system.solarSystemName}</span>
                            <button
                              onClick={() => handleToggleFulfillmentSystem(system.solarSystemId)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                system.salesOrderRelevant ? 'bg-blue-600' : 'bg-gray-600'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  system.salesOrderRelevant ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Hangars Tab */}
              {fulfillmentSettingsTab === 'hangars' && (
                <>
                  <p className="text-gray-400 text-sm mb-4">
                    Select which corporation hangars should be included when calculating "In Stock" values.
                  </p>

                  <div className="flex-1 overflow-y-auto min-h-0 mb-4">
                    {loadingFulfillmentHangars ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {([1, 2, 3, 4, 5, 6, 7] as const).map((hangarNum) => {
                          const hangarKey = `corpHangar${hangarNum}` as keyof typeof fulfillmentHangars;
                          return (
                            <div
                              key={hangarKey}
                              className="flex items-center justify-between p-3 bg-gray-700 rounded-lg"
                            >
                              <span className="text-white">Hangar {hangarNum}</span>
                              <button
                                onClick={() => handleToggleFulfillmentHangar(hangarKey)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                  fulfillmentHangars[hangarKey] ? 'bg-blue-600' : 'bg-gray-600'
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    fulfillmentHangars[hangarKey] ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Footer with Save/Cancel buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button
                  onClick={() => setShowFulfillmentSettings(false)}
                  disabled={savingFulfillmentSettings}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveFulfillmentSettings}
                  disabled={savingFulfillmentSettings || loadingFulfillmentSystems}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg transition-colors"
                >
                  {savingFulfillmentSettings && (
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default SalesOrders;
